import React, { useEffect, useState, Component } from 'react';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Modal } from '../components/ui/Modal';
import { Textarea } from '../components/ui/Textarea';
import { DatePicker } from '../components/ui/DatePicker';
import { Tooltip } from '../components/ui/Tooltip';
import { Pagination } from '../components/ui/Pagination';
import { ColumnCustomizer } from '../components/ColumnCustomizer';
import { CalendarView } from '../components/CalendarView';
import { useSearch } from '../hooks/useSearch';
import { useFilters } from '../hooks/useFilters';
import { usePagination } from '../hooks/usePagination';
import { useNotifications } from '../contexts/NotificationContext';
import { ProgressiveNumberInput } from '../components/ProgressiveNumberInput';
import { GenerateActivityWizardModal } from '../components/operations/GenerateActivityWizardModal';
import { WorkerDetailHeader } from '../components/workers/WorkerDetailHeader';
import { WorkerAnagraficaSections } from '../components/workers/WorkerAnagraficaSections';
import { WorkerAnagraficaForm } from '../components/workers/WorkerAnagraficaForm';
import { WorkerTabs, WorkerTab } from '../components/workers/WorkerTabs';
import { WorkerTrainingTab } from '../components/workers/WorkerTrainingTab';
import { WorkerHealthFields } from '../components/workers/WorkerHealthFields';
import { AssignTrainingModal } from '../components/workers/AssignTrainingModal';
import { TrainingImportModal } from '../components/workers/TrainingImportModal';
import { mockData } from '../services/mockData';
import { AttachmentsField, AttachmentsList } from '../components/ui/AttachmentsField';
import { getWorkerRuolo } from '../utils/workers';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Eye, Edit, Trash2, Plus, Save, CheckCircle2, AlertCircle, HardHat, Calendar, FileText, Receipt, GraduationCap, ClipboardList, AlertTriangle, TrendingUp, Wrench, Hammer, Mail, ExternalLink, X, Search, Filter, Download, List, CalendarIcon, LayoutGrid, Euro, FileSpreadsheet } from 'lucide-react';
// ─── Section Config ───────────────────────────────────────────────────────────
const sectionConfig: Record<string, {
  title: string;
  description: string;
  icon: React.ElementType;
  newLabel: string;
}> = {
  workers: {
    title: 'Risorse umane',
    description: 'Dipendenti, formazione e sorveglianza sanitaria.',
    icon: HardHat,
    newLabel: 'Nuovo lavoratore'
  },
  deadlines: {
    title: 'Scadenze',
    description: 'Scadenze tecniche e normative.',
    icon: Calendar,
    newLabel: 'Nuova scadenza'
  },
  contracts: {
    title: 'Offerte e contratti',
    description: 'Offerte, contratti attivi e storico.',
    icon: FileText,
    newLabel: 'Nuovo contratto'
  },
  invoices: {
    title: 'Fatturazione',
    description: 'Fatture, proforma e scadenziario.',
    icon: Receipt,
    newLabel: 'Nuova fattura'
  },
  training: {
    title: 'Formazione',
    description: 'Corsi, iscrizioni e certificazioni.',
    icon: GraduationCap,
    newLabel: 'Nuovo corso'
  },
  operations: {
    title: 'Attività operative',
    description: 'Pianificazione e reportistica delle attività.',
    icon: ClipboardList,
    newLabel: 'Nuova attività'
  },
  non_conformities: {
    title: 'Non conformità',
    description: 'NC, reclami e azioni correttive.',
    icon: AlertTriangle,
    newLabel: 'Nuova NC'
  },
  improvements: {
    title: 'Miglioramenti',
    description: 'Proposte e piani di miglioramento.',
    icon: TrendingUp,
    newLabel: 'Nuovo miglioramento'
  },
  equipment: {
    title: 'Asset e manutenzioni',
    description: 'Asset aziendali e manutenzioni programmate.',
    icon: Wrench,
    newLabel: 'Nuovo asset'
  },
  maintenance: {
    title: 'Manutenzione',
    description: 'Storico interventi e manutenzioni.',
    icon: Hammer,
    newLabel: 'Nuova manutenzione'
  }
};
// ─── Columns Config ───────────────────────────────────────────────────────────
const columnsConfigMap: Record<string, {
  key: string;
  label: string;
}[]> = {
  workers: [{
    key: 'name',
    label: 'Lavoratore'
  }, {
    key: 'role',
    label: 'Mansione'
  }, {
    key: 'trainingStatus',
    label: 'Formazione'
  }, {
    key: 'healthStatus',
    label: 'Sorv. sanitaria'
  }, {
    key: 'nextExpiry',
    label: 'Prox. scadenza'
  }, {
    key: 'actions',
    label: 'Azioni'
  }],
  deadlines: [{
    key: 'code',
    label: 'Codice'
  }, {
    key: 'description',
    label: 'Descrizione'
  }, {
    key: 'type',
    label: 'Tipologia'
  }, {
    key: 'date',
    label: 'Data scadenza'
  }, {
    key: 'notifyClient',
    label: 'Notifica'
  }, {
    key: 'responsible',
    label: 'Responsabile'
  }, {
    key: 'status',
    label: 'Stato'
  }, {
    key: 'actions',
    label: 'Azioni'
  }],
  contracts: [{
    key: 'code',
    label: 'Codice'
  }, {
    key: 'object',
    label: 'Oggetto'
  }, {
    key: 'type',
    label: 'Tipo'
  }, {
    key: 'value',
    label: 'Valore'
  }, {
    key: 'status',
    label: 'Stato'
  }, {
    key: 'date',
    label: 'Data'
  }, {
    key: 'actions',
    label: 'Azioni'
  }],
  invoices: [{
    key: 'number',
    label: 'Numero'
  }, {
    key: 'description',
    label: 'Descrizione'
  }, {
    key: 'amount',
    label: 'Importo'
  }, {
    key: 'status',
    label: 'Stato'
  }, {
    key: 'dueDate',
    label: 'Scadenza'
  }, {
    key: 'actions',
    label: 'Azioni'
  }],
  training: [{
    key: 'code',
    label: 'Codice'
  }, {
    key: 'title',
    label: 'Titolo'
  }, {
    key: 'type',
    label: 'Tipologia'
  }, {
    key: 'mode',
    label: 'Modalità'
  }, {
    key: 'date',
    label: 'Data'
  }, {
    key: 'enrolled',
    label: 'Iscritti'
  }, {
    key: 'status',
    label: 'Stato'
  }, {
    key: 'actions',
    label: 'Azioni'
  }],
  operations: [{
    key: 'code',
    label: 'Codice'
  }, {
    key: 'activityType',
    label: 'Attività'
  }, {
    key: 'ambito',
    label: 'Ambito'
  }, {
    key: 'contract',
    label: 'Contratto'
  }, {
    key: 'operator',
    label: 'Operatore'
  }, {
    key: 'status',
    label: 'Stato'
  }, {
    key: 'deadline',
    label: 'Scadenza'
  }, {
    key: 'actions',
    label: 'Azioni'
  }],
  non_conformities: [{
    key: 'number',
    label: 'Codice'
  }, {
    key: 'type',
    label: 'Tipologia'
  }, {
    key: 'origin',
    label: 'Origine'
  }, {
    key: 'description',
    label: 'Descrizione'
  }, {
    key: 'date',
    label: 'Data rilev.'
  }, {
    key: 'responsible',
    label: 'Responsabile'
  }, {
    key: 'status',
    label: 'Stato'
  }, {
    key: 'actions',
    label: 'Azioni'
  }],
  improvements: [{
    key: 'number',
    label: 'Codice'
  }, {
    key: 'subject',
    label: 'Oggetto'
  }, {
    key: 'description',
    label: 'Descrizione'
  }, {
    key: 'date',
    label: 'Data proposta'
  }, {
    key: 'responsible',
    label: 'Responsabile'
  }, {
    key: 'status',
    label: 'Stato'
  }, {
    key: 'actions',
    label: 'Azioni'
  }],
  equipment: [{
    key: 'code',
    label: 'Codice'
  }, {
    key: 'name',
    label: 'Asset'
  }, {
    key: 'type',
    label: 'Tipologia'
  }, {
    key: 'lastMaintenance',
    label: 'Ultima manut.'
  }, {
    key: 'nextMaintenance',
    label: 'Prox. manut.'
  }, {
    key: 'responsible',
    label: 'Responsabile'
  }, {
    key: 'maintenanceType',
    label: 'Tipo manut.'
  }, {
    key: 'actions',
    label: 'Azioni'
  }],
  maintenance: [{
    key: 'code',
    label: 'Codice'
  }, {
    key: 'equipment',
    label: 'Asset'
  }, {
    key: 'type',
    label: 'Tipo'
  }, {
    key: 'date',
    label: 'Data esecuzione'
  }, {
    key: 'result',
    label: 'Esito'
  }, {
    key: 'nextDate',
    label: 'Prox. scadenza'
  }, {
    key: 'responsible',
    label: 'Responsabile'
  }, {
    key: 'actions',
    label: 'Azioni'
  }]
};
// ─── Mock Data ────────────────────────────────────────────────────────────────
const mockWorkers = [{
  id: 1,
  name: 'Mario Rossi',
  role: 'Operaio',
  trainingStatus: 'valid',
  healthStatus: 'fit',
  nextExpiry: '12/05/2025',
  code: 'LAV-001',
  fiscalCode: 'RSSMRA80A01A390X',
  birthDate: '01/01/1980',
  birthPlace: 'Arezzo',
  birthProvince: 'AR',
  nationality: 'Italiana',
  email: 'mario.rossi@email.it',
  department: 'Produzione',
  sede: 'Sede principale',
  employmentType: 'Dipendente',
  contractType: 'Tempo indeterminato',
  safetyRoles: ['antincendio']
}, {
  id: 2,
  name: 'Luigi Verdi',
  role: 'Impiegato',
  trainingStatus: 'expiring',
  healthStatus: 'fit',
  nextExpiry: '10/06/2025',
  code: 'LAV-002',
  fiscalCode: 'VRDLGU85B12D612Y',
  birthDate: '12/02/1985',
  birthPlace: 'Firenze',
  birthProvince: 'FI',
  nationality: 'Italiana',
  email: 'luigi.verdi@email.it',
  department: 'Amministrazione',
  sede: 'Sede principale',
  employmentType: 'Dipendente',
  contractType: 'Tempo indeterminato',
  safetyRoles: []
}, {
  id: 3,
  name: 'Anna Bianchi',
  role: 'Tecnico',
  trainingStatus: 'expired',
  healthStatus: 'fit',
  nextExpiry: '15/01/2025',
  code: 'LAV-003',
  fiscalCode: 'BNCNNA90C45I726Z',
  birthDate: '05/03/1990',
  birthPlace: 'Siena',
  birthProvince: 'SI',
  nationality: 'Italiana',
  email: 'anna.bianchi@email.it',
  department: 'Ufficio tecnico',
  sede: 'Sede operativa',
  employmentType: 'Dipendente',
  contractType: 'Tempo determinato',
  safetyRoles: ['primosoccorso']
}, {
  id: 4,
  name: 'Carlo Neri',
  role: 'Magazziniere',
  trainingStatus: 'valid',
  healthStatus: 'fit',
  nextExpiry: '20/08/2025',
  code: 'LAV-004',
  fiscalCode: 'NRECRL78D10A390K',
  birthDate: '10/04/1978',
  birthPlace: 'Arezzo',
  birthProvince: 'AR',
  nationality: 'Italiana',
  email: 'carlo.neri@email.it',
  department: 'Logistica',
  sede: 'Stabilimento 1',
  employmentType: 'Dipendente',
  contractType: 'Tempo indeterminato',
  safetyRoles: []
}, {
  id: 5,
  name: 'Giulia Russo',
  role: 'Amministrativa',
  trainingStatus: 'valid',
  healthStatus: 'fit',
  nextExpiry: '05/09/2025',
  code: 'LAV-005',
  fiscalCode: 'RSSGLI92E55G702M',
  birthDate: '15/05/1992',
  birthPlace: 'Pisa',
  birthProvince: 'PI',
  nationality: 'Italiana',
  email: 'giulia.russo@email.it',
  department: 'Amministrazione',
  sede: 'Sede principale',
  employmentType: 'Dipendente',
  contractType: 'Part-time',
  safetyRoles: []
}, {
  id: 6,
  name: 'Fabio Conti',
  role: 'Preposto',
  trainingStatus: 'expiring',
  healthStatus: 'fit',
  nextExpiry: '01/07/2025',
  code: 'LAV-006',
  fiscalCode: 'CNTFBA75H20E202P',
  birthDate: '20/06/1975',
  birthPlace: 'Grosseto',
  birthProvince: 'GR',
  nationality: 'Italiana',
  email: 'fabio.conti@email.it',
  department: 'Produzione',
  sede: 'Stabilimento 1',
  employmentType: 'Dipendente',
  contractType: 'Tempo indeterminato',
  safetyRoles: ['preposto']
}, {
  id: 7,
  name: 'Sara Mancini',
  role: 'RLS',
  trainingStatus: 'valid',
  healthStatus: 'fit',
  nextExpiry: '18/10/2025',
  code: 'LAV-007',
  fiscalCode: 'MNCSRA88L60A390R',
  birthDate: '20/07/1988',
  birthPlace: 'Arezzo',
  birthProvince: 'AR',
  nationality: 'Italiana',
  email: 'sara.mancini@email.it',
  department: 'Qualità',
  sede: 'Sede operativa',
  employmentType: 'Dipendente',
  contractType: 'Tempo indeterminato',
  safetyRoles: ['rls']
}];
const mockDeadlines = [{
  id: 1,
  code: 'SCA-2025/001',
  description: 'Rinnovo DVR',
  type: 'Audit interno',
  date: '2025-06-15',
  notifyClient: 'Sì',
  responsible: 'Alessio',
  status: 'In scadenza'
}, {
  id: 2,
  code: 'SCA-2025/002',
  description: 'Verifica estintori',
  type: 'Prova di emergenza',
  date: '2025-06-20',
  notifyClient: 'No',
  responsible: 'Lorenzo',
  status: 'Pianificata'
}, {
  id: 3,
  code: 'SCA-2025/003',
  description: 'Tampone acque',
  type: 'Tampone alimentare',
  date: '2025-07-01',
  notifyClient: 'Sì',
  responsible: 'Gian Franco',
  status: 'Pianificata'
}, {
  id: 4,
  code: 'SCA-2025/004',
  description: 'Riunione sicurezza',
  type: 'Riunione annuale',
  date: '2025-05-30',
  notifyClient: 'Sì',
  responsible: 'Alessio',
  status: 'Urgente'
}, {
  id: 5,
  code: 'SCA-2025/005',
  description: 'Campionamento prodotti',
  type: 'Campionamento alimentare',
  date: '2025-08-10',
  notifyClient: 'No',
  responsible: 'Tiziano',
  status: 'Pianificata'
}, {
  id: 6,
  code: 'SCA-2025/006',
  description: 'Report annuale sicurezza',
  type: 'Report annuale',
  date: '2025-12-31',
  notifyClient: 'Sì',
  responsible: 'Alessio',
  status: 'Pianificata'
}, {
  id: 7,
  code: 'SCA-2025/007',
  description: 'Corso aggiornamento RLS',
  type: 'Audit interno',
  date: '2025-05-28',
  notifyClient: 'No',
  responsible: 'Lorenzo',
  status: 'Urgente'
}];
const mockContracts = [{
  id: 1,
  code: 'CTR-2025/001',
  object: 'Sicurezza Annuale',
  type: 'Contratto',
  value: '€ 3.200',
  status: 'Attivo',
  date: '01/01/2025'
}, {
  id: 2,
  code: 'CTR-2025/002',
  object: 'HACCP Base',
  type: 'Contratto',
  value: '€ 1.500',
  status: 'Attivo',
  date: '15/02/2025'
}, {
  id: 3,
  code: 'PREV-2025/001',
  object: 'Formazione Lavoratori',
  type: 'Offerta',
  value: '€ 2.800',
  status: 'In attesa',
  date: '10/05/2025'
}, {
  id: 4,
  code: 'CTR-2024/008',
  object: 'Privacy GDPR',
  type: 'Contratto',
  value: '€ 900',
  status: 'Scaduto',
  date: '01/01/2024'
}, {
  id: 5,
  code: 'PREV-2025/002',
  object: 'Audit Interno',
  type: 'Offerta',
  value: '€ 1.200',
  status: 'Bozza',
  date: '20/05/2025'
}, {
  id: 6,
  code: 'CTR-2025/003',
  object: 'Sorveglianza Sanitaria',
  type: 'Contratto',
  value: '€ 2.100',
  status: 'Attivo',
  date: '01/03/2025'
}, {
  id: 7,
  code: 'PREV-2025/003',
  object: 'Aggiornamento DVR',
  type: 'Offerta',
  value: '€ 750',
  status: 'In attesa',
  date: '12/05/2025'
}];
const mockInvoices = [{
  id: 1,
  number: 'FT-2025/045',
  description: 'Canone sicurezza Q1',
  amount: '€ 800',
  status: 'Pagata',
  dueDate: '15/04/2025'
}, {
  id: 2,
  number: 'FT-2025/052',
  description: 'Corso formazione HACCP',
  amount: '€ 1.200',
  status: 'In attesa',
  dueDate: '10/05/2025'
}, {
  id: 3,
  number: 'PRF-2025/012',
  description: 'Consulenza privacy',
  amount: '€ 450',
  status: 'Bozza',
  dueDate: '20/05/2025'
}, {
  id: 4,
  number: 'FT-2025/038',
  description: 'Sopralluogo periodico',
  amount: '€ 350',
  status: 'Pagata',
  dueDate: '01/04/2025'
}, {
  id: 5,
  number: 'FT-2025/061',
  description: 'Aggiornamento DVR',
  amount: '€ 750',
  status: 'Scaduta',
  dueDate: '30/04/2025'
}, {
  id: 6,
  number: 'FT-2025/067',
  description: 'Sorveglianza sanitaria',
  amount: '€ 520',
  status: 'In attesa',
  dueDate: '15/06/2025'
}, {
  id: 7,
  number: 'PRF-2025/015',
  description: 'Audit interno',
  amount: '€ 600',
  status: 'Bozza',
  dueDate: '30/06/2025'
}];
const mockTraining = [{
  id: 1,
  code: 'CRS-001',
  title: 'Sicurezza Generale Lavoratori',
  type: 'Sicurezza',
  mode: 'Sede SEF',
  date: '2025-05-20',
  enrolled: 5,
  status: 'In corso'
}, {
  id: 2,
  code: 'CRS-002',
  title: 'HACCP Responsabile',
  type: 'HACCP',
  mode: 'Sede cliente',
  date: '2025-05-22',
  enrolled: 3,
  status: 'In corso'
}, {
  id: 3,
  code: 'CRS-003',
  title: 'Antincendio Rischio Medio',
  type: 'Sicurezza',
  mode: 'Sede SEF',
  date: '2025-06-05',
  enrolled: 4,
  status: 'Programmato'
}, {
  id: 4,
  code: 'CRS-004',
  title: 'Primo Soccorso Gruppo B',
  type: 'Sicurezza',
  mode: 'A distanza',
  date: '2025-06-10',
  enrolled: 6,
  status: 'Programmato'
}, {
  id: 5,
  code: 'CRS-005',
  title: 'Aggiornamento RLS',
  type: 'Sicurezza',
  mode: 'Sede SEF',
  date: '2025-04-15',
  enrolled: 2,
  status: 'Completato'
}, {
  id: 6,
  code: 'CRS-006',
  title: 'Preposto Aggiornamento',
  type: 'Sicurezza',
  mode: 'Sede SEF',
  date: '2025-03-10',
  enrolled: 3,
  status: 'Completato'
}, {
  id: 7,
  code: 'CRS-007',
  title: 'Privacy GDPR Base',
  type: 'Privacy',
  mode: 'A distanza',
  date: '2025-07-01',
  enrolled: 0,
  status: 'Programmato'
}];
const mockOperations = [{
  id: 1,
  code: 'ATT-2025/001',
  activityType: 'Sopralluogo periodico',
  ambito: 'Sicurezza',
  contract: 'CTR-2025/001',
  operator: 'Alessio',
  zone: 'Arezzo',
  status: 'completo',
  deadline: '30/04/2025'
}, {
  id: 2,
  code: 'ATT-2025/002',
  activityType: 'Backoffice – Redazione documentazione',
  ambito: 'Privacy',
  contract: 'CTR-2025/002',
  operator: 'Lorenzo',
  zone: 'Arezzo',
  status: 'in_svolgimento_attivo',
  deadline: '15/06/2025'
}, {
  id: 3,
  code: 'ATT-2025/003',
  activityType: 'Sopralluogo iniziale e check list',
  ambito: 'HACCP',
  contract: 'CTR-2025/002',
  operator: 'Gian Franco',
  zone: 'Arezzo',
  status: 'calendarizzato',
  deadline: '20/06/2025'
}, {
  id: 4,
  code: 'ATT-2025/004',
  activityType: 'Campionamenti e tamponi alimentari',
  ambito: 'HACCP',
  contract: 'CTR-2025/002',
  operator: 'Alessio',
  zone: 'Arezzo',
  status: 'pianificato',
  deadline: '30/07/2025'
}, {
  id: 5,
  code: 'ATT-2025/005',
  activityType: 'Riunione in ufficio',
  ambito: 'Sicurezza',
  contract: 'CTR-2025/001',
  operator: 'Lorenzo',
  zone: 'Arezzo',
  status: 'da_pianificare',
  deadline: '31/08/2025'
}, {
  id: 6,
  code: 'ATT-2025/006',
  activityType: 'Consulenza via email',
  ambito: 'Ambiente',
  contract: 'CTR-2025/003',
  operator: 'Tiziano',
  zone: 'Arezzo',
  status: 'completo',
  deadline: '10/04/2025'
}, {
  id: 7,
  code: 'ATT-2025/007',
  activityType: 'Sopralluogo consegna',
  ambito: 'Sicurezza',
  contract: 'CTR-2025/001',
  operator: 'Alessio',
  zone: 'Arezzo',
  status: 'in_svolgimento_sospeso',
  deadline: '25/06/2025'
}];
const mockNonConformities = [{
  id: 1,
  number: 'NC-2025/001',
  type: 'Non conformità maggiore',
  origin: 'Interna',
  description: 'Mancanza DPI in reparto produzione',
  date: '2025-04-12',
  responsible: 'Lorenzo',
  status: 'Aperta'
}, {
  id: 2,
  number: 'NC-2025/002',
  type: 'Non conformità minore',
  origin: 'Cliente (reclamo)',
  description: 'Documentazione incompleta',
  date: '2025-05-05',
  responsible: 'Alessio',
  status: 'Chiusa'
}, {
  id: 3,
  number: 'NC-2025/003',
  type: 'Osservazione',
  origin: 'Interna',
  description: 'Segnaletica non aggiornata',
  date: '2025-05-15',
  responsible: 'Gian Franco',
  status: 'Aperta'
}, {
  id: 4,
  number: 'NC-2025/004',
  type: 'Non conformità minore',
  origin: 'Fornitore',
  description: 'Prodotto non conforme alle specifiche',
  date: '2025-03-20',
  responsible: 'Lorenzo',
  status: 'Chiusa'
}, {
  id: 5,
  number: 'NC-2025/005',
  type: 'Osservazione',
  origin: 'Interna',
  description: 'Procedura non rispettata',
  date: '2025-05-22',
  responsible: 'Alessio',
  status: 'Sospesa'
}, {
  id: 6,
  number: 'NC-2025/006',
  type: 'Non conformità maggiore',
  origin: 'Cliente (reclamo)',
  description: 'Ritardo nella consegna documentazione',
  date: '2025-04-30',
  responsible: 'Tiziano',
  status: 'Aperta'
}];
const mockImprovements = [{
  id: 1,
  number: 'MIG-2025/001',
  subject: 'Digitalizzazione archivio DVR',
  description: 'Proposta per archiviazione digitale di tutti i DVR',
  date: '2025-03-01',
  responsible: 'Alessio',
  status: 'Approvato'
}, {
  id: 2,
  number: 'MIG-2025/002',
  subject: 'Formazione extra sicurezza',
  description: 'Corso aggiuntivo per addetti al magazzino',
  date: '2025-04-15',
  responsible: 'Lorenzo',
  status: 'In valutazione'
}, {
  id: 3,
  number: 'MIG-2025/003',
  subject: 'Nuovi DPI ergonomici',
  description: 'Sostituzione DPI con modelli certificati ergonomici',
  date: '2025-01-10',
  responsible: 'Gian Franco',
  status: 'Approvato'
}, {
  id: 4,
  number: 'MIG-2025/004',
  subject: 'Aggiornamento piano emergenza',
  description: 'Revisione completa del piano di emergenza',
  date: '2025-05-01',
  responsible: 'Alessio',
  status: 'In valutazione'
}, {
  id: 5,
  number: 'MIG-2025/005',
  subject: 'Segnaletica bilingue',
  description: 'Aggiunta segnaletica in italiano e inglese',
  date: '2025-02-20',
  responsible: 'Tiziano',
  status: 'Sospesa'
}, {
  id: 6,
  number: 'MIG-2025/006',
  subject: 'Check-list digitale sopralluoghi',
  description: 'App mobile per compilazione check-list in loco',
  date: '2025-05-10',
  responsible: 'Lorenzo',
  status: 'In valutazione'
}];
const mockEquipment = [{
  id: 1,
  code: 'EQP-001',
  name: 'Fonometro Brüel & Kjær',
  type: 'Strumento Misura',
  lastMaintenance: '2025-01-10',
  nextMaintenance: '2026-01-10',
  responsible: 'Alessio',
  maintenanceType: 'Ordinaria'
}, {
  id: 2,
  code: 'EQP-002',
  name: 'Carrello elevatore Toyota',
  type: 'Veicolo',
  lastMaintenance: '2025-03-15',
  nextMaintenance: '2025-09-15',
  responsible: 'Lorenzo',
  maintenanceType: 'Ordinaria'
}, {
  id: 3,
  code: 'EQP-003',
  name: 'Pressa idraulica 50T',
  type: 'Attrezzatura',
  lastMaintenance: '2024-12-01',
  nextMaintenance: '2025-06-01',
  responsible: 'Gian Franco',
  maintenanceType: 'Straordinaria'
}, {
  id: 4,
  code: 'EQP-004',
  name: 'Notebook Dell Latitude',
  type: 'Hardware',
  lastMaintenance: '2025-02-20',
  nextMaintenance: '2026-02-20',
  responsible: 'Alessio',
  maintenanceType: 'Ordinaria'
}, {
  id: 5,
  code: 'EQP-005',
  name: 'Trapano a colonna',
  type: 'Attrezzatura',
  lastMaintenance: '2025-04-05',
  nextMaintenance: '2025-10-05',
  responsible: 'Lorenzo',
  maintenanceType: 'Ordinaria'
}, {
  id: 6,
  code: 'EQP-006',
  name: 'Misuratore CO2',
  type: 'Strumento Misura',
  lastMaintenance: '2025-01-25',
  nextMaintenance: '2026-01-25',
  responsible: 'Tiziano',
  maintenanceType: 'Ordinaria'
}, {
  id: 7,
  code: 'EQP-007',
  name: 'Autocarro Iveco Daily',
  type: 'Veicolo',
  lastMaintenance: '2025-04-01',
  nextMaintenance: '2025-10-01',
  responsible: 'Gian Franco',
  maintenanceType: 'Straordinaria'
}];
const mockMaintenance = [{
  id: 1,
  code: 'MNT-2025/001',
  equipment: 'Fonometro Brüel & Kjær',
  type: 'Ordinaria',
  date: '2025-01-10',
  result: 'Positivo',
  nextDate: '2026-01-10',
  responsible: 'Alessio'
}, {
  id: 2,
  code: 'MNT-2025/002',
  equipment: 'Carrello elevatore Toyota',
  type: 'Ordinaria',
  date: '2025-03-15',
  result: 'Positivo',
  nextDate: '2025-09-15',
  responsible: 'Lorenzo'
}, {
  id: 3,
  code: 'MNT-2025/003',
  equipment: 'Pressa idraulica 50T',
  type: 'Straordinaria',
  date: '2024-12-01',
  result: 'Negativo',
  nextDate: '2025-06-01',
  responsible: 'Gian Franco'
}, {
  id: 4,
  code: 'MNT-2025/004',
  equipment: 'Notebook Dell Latitude',
  type: 'Ordinaria',
  date: '2025-02-20',
  result: 'Positivo',
  nextDate: '2026-02-20',
  responsible: 'Alessio'
}, {
  id: 5,
  code: 'MNT-2025/005',
  equipment: 'Trapano a colonna',
  type: 'Ordinaria',
  date: '2025-04-05',
  result: 'Positivo',
  nextDate: '2025-10-05',
  responsible: 'Lorenzo'
}, {
  id: 6,
  code: 'MNT-2025/006',
  equipment: 'Misuratore CO2',
  type: 'Ordinaria',
  date: '2025-01-25',
  result: 'Positivo',
  nextDate: '2026-01-25',
  responsible: 'Tiziano'
}, {
  id: 7,
  code: 'MNT-2025/007',
  equipment: 'Autocarro Iveco Daily',
  type: 'Straordinaria',
  date: '2025-04-01',
  result: 'Positivo',
  nextDate: '2025-10-01',
  responsible: 'Gian Franco'
}];
// ─── Helpers ──────────────────────────────────────────────────────────────────
const trainingStatusKey: Record<string, string> = {
  'In regola': 'valid',
  'In scadenza': 'expiring',
  Scaduto: 'expired'
};
function companyWorkers(clientName: string): any[] | null {
  const client = mockData.getClientByName(clientName);
  if (!client) return null;
  const workers = mockData.getWorkersByClient(client.id);
  if (workers.length === 0) return null;
  return workers.map(w => ({
    ...w,
    trainingStatus: trainingStatusKey[w.trainingStatus] || 'valid'
  }));
}
function getSectionMockData(sectionId: string): any[] {
  switch (sectionId) {
    case 'workers':
      return mockWorkers;
    case 'deadlines':
      return mockDeadlines;
    case 'contracts':
      return mockContracts;
    case 'invoices':
      return mockInvoices;
    case 'training':
      return mockTraining;
    case 'operations':
      return mockOperations;
    case 'non_conformities':
      return mockNonConformities;
    case 'improvements':
      return mockImprovements;
    case 'equipment':
      return mockEquipment;
    case 'maintenance':
      return mockMaintenance;
    default:
      return [];
  }
}
function getSectionSearchFields(sectionId: string): string[] {
  switch (sectionId) {
    case 'workers':
      return ['name', 'role'];
    case 'deadlines':
      return ['description', 'type', 'code'];
    case 'contracts':
      return ['code', 'object'];
    case 'invoices':
      return ['number', 'description'];
    case 'training':
      return ['title', 'code', 'type'];
    case 'operations':
      return ['activityType', 'ambito', 'code'];
    case 'non_conformities':
      return ['number', 'description', 'origin'];
    case 'improvements':
      return ['number', 'subject', 'description'];
    case 'equipment':
      return ['code', 'name', 'type'];
    case 'maintenance':
      return ['code', 'equipment'];
    default:
      return [];
  }
}
function getDefaultColumns(sectionId: string): string[] {
  return (columnsConfigMap[sectionId] || []).map(c => c.key);
}
function getLockedColumns(sectionId: string): string[] {
  return sectionId === 'workers' ? ['name'] : ['actions'];
}
const statusColors: Record<string, string> = {
  da_pianificare: 'neutral',
  pianificato: 'info',
  calendarizzato: 'primary',
  in_svolgimento_attivo: 'success',
  in_svolgimento_sospeso: 'warning',
  in_svolgimento_stop: 'danger',
  completo: 'success'
};
const statusLabels: Record<string, string> = {
  da_pianificare: 'Da pianificare',
  pianificato: 'Pianificata',
  calendarizzato: 'Calendarizzata',
  in_svolgimento_attivo: 'Attiva',
  in_svolgimento_sospeso: 'Sospesa',
  in_svolgimento_stop: 'Stop',
  completo: 'Completa'
};
// ─── Shared Delete Modal ──────────────────────────────────────────────────────
function DeleteModal({
  isOpen,
  onClose,
  onConfirm,
  itemLabel,
  deleteSuccess
}: {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  itemLabel: string;
  deleteSuccess: boolean;
}) {
  return <Modal isOpen={isOpen} onClose={onClose} title="Conferma eliminazione" size="sm" data-id="element-2412" data-mp="1n40ixa3ypaxl">
      <AnimatePresence mode="wait" data-id="element-2413" data-mp="3blk5kzaaud81">
        {deleteSuccess ? <motion.div key="success" initial={{
        opacity: 0,
        scale: 0.8
      }} animate={{
        opacity: 1,
        scale: 1
      }} className="flex flex-col items-center justify-center py-12" data-id="element-2414" data-mp="3bksarqoj0jit">
            <motion.div initial={{
          scale: 0
        }} animate={{
          scale: 1
        }} transition={{
          type: 'spring',
          stiffness: 150,
          damping: 15,
          delay: 0.1
        }} className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4" data-id="element-2415" data-mp="2xs2orm8zzelp">
              <CheckCircle2 className="w-10 h-10 text-green-500" data-id="element-2416" data-mp="i3kg53usgjxk" />
            </motion.div>
            <p className="text-lg font-semibold text-slate-800" data-id="element-2417" data-mp="d47lvy1l8aky">
              Eliminato con successo
            </p>
          </motion.div> : <motion.div key="confirm" initial={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} data-id="element-2418" data-mp="13c0qm9jf0l2m">
            <div className="space-y-6" data-id="element-2419" data-mp="1c4vp8kednijf">
              <div className="bg-red-50 p-4 rounded-lg border border-red-100 flex gap-3" data-id="element-2420" data-mp="184k3f4aa3c6d">
                <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0" data-id="element-2421" data-mp="186xwj7leeeba" />
                <div data-id="element-2422" data-mp="kf9b8b6zuq3u">
                  <p className="text-sm text-red-700 font-medium" data-id="element-2423" data-mp="kgu31t1xbuy0">
                    Attenzione: questa azione è irreversibile.
                  </p>
                  <p className="text-sm text-red-600 mt-1" data-id="element-2424" data-mp="tgqq5q8o0lqy">
                    L'elemento <strong data-id="element-2425" data-mp="og2z54kfs4wq">{itemLabel}</strong> verrà eliminato
                    definitivamente.
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4" data-id="element-2426" data-mp="24wxe1rjj0n60">
                <Button variant="flat" color="default" onClick={onClose} data-id="element-2427" data-mp="1zuq04wf4yx11">
                  Annulla
                </Button>
                <Button color="danger" onClick={onConfirm} startContent={<Trash2 className="h-4 w-4" data-id="element-2429" data-mp="2cc94p09d9wbp" />} data-id="element-2428" data-mp="28bgrxx1ad9ao">
                  Elimina
                </Button>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </Modal>;
}
// ─── Save Success Overlay ─────────────────────────────────────────────────────
function SaveSuccessView({
  label
}: {
  label: string;
}) {
  return <motion.div key="success" initial={{
    opacity: 0,
    scale: 0.8
  }} animate={{
    opacity: 1,
    scale: 1
  }} className="flex flex-col items-center justify-center py-16" data-id="element-2430" data-mp="2w11gqwk4f0rv">
      <motion.div initial={{
      scale: 0
    }} animate={{
      scale: 1
    }} transition={{
      type: 'spring',
      stiffness: 200,
      damping: 15
    }} className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mb-4" data-id="element-2431" data-mp="2ma6tppz1vx5d">
        <CheckCircle2 className="w-12 h-12 text-green-500" data-id="element-2432" data-mp="3jp7xik3inloj" />
      </motion.div>
      <p className="text-lg font-semibold text-slate-800" data-id="element-2433" data-mp="3eqn1kwvaro5w">{label}</p>
    </motion.div>;
}
// ─── Props ────────────────────────────────────────────────────────────────────
interface ClientSectionPageProps {
  sectionId: string;
  clientName: string;
  onBack: () => void;
}
// ─── Main Component ───────────────────────────────────────────────────────────
export function ClientSectionPage({
  sectionId,
  clientName,
  onBack
}: ClientSectionPageProps) {
  const {
    addNotification
  } = useNotifications();
  const config = sectionConfig[sectionId] || sectionConfig['workers'];
  const Icon = config.icon;
  // ─── Shared modal state ───────────────────────────────────────────────────
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isGenerateActivityOpen, setIsGenerateActivityOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [formData, setFormData] = useState<any>({});
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const [workerFormTab, setWorkerFormTab] = useState<WorkerTab>('info');
  useEffect(() => {
    if (isEditOpen) setWorkerFormTab('info');
  }, [isEditOpen]);
  // ─── Toolbar state ────────────────────────────────────────────────────────
  const [showFilters, setShowFilters] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState<string[]>(getDefaultColumns(sectionId));
  const [viewMode, setViewMode] = useState<'list' | 'calendar'>('list');
  // ─── Hooks (called unconditionally at top level) ──────────────────────────
  // Risorse umane of this company come from the same database used by the
  // aggregated view and by the Formazione module
  const sectionData = React.useMemo(() => sectionId === 'workers' ? companyWorkers(clientName) || mockWorkers : getSectionMockData(sectionId), [sectionId, clientName]);
  const [showAssignTraining, setShowAssignTraining] = useState(false);
  const [showTrainingImport, setShowTrainingImport] = useState(false);
  const searchFields = getSectionSearchFields(sectionId);
  const {
    filters,
    setFilter,
    clearFilters,
    filteredData: filterResult
  } = useFilters(sectionData);
  const {
    searchTerm,
    setSearchTerm,
    filteredData: searchResult
  } = useSearch(filterResult, searchFields);
  const {
    currentPage,
    totalPages,
    paginatedData,
    goToPage,
    pageSize,
    setPageSize,
    totalItems,
    setCurrentPage
  } = usePagination(searchResult);
  // Reset toolbar state when section changes
  useEffect(() => {
    clearFilters();
    setSearchTerm('');
    setShowFilters(false);
    setViewMode('list');
    setVisibleColumns(getDefaultColumns(sectionId));
    setCurrentPage(1);
  }, [sectionId]);
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, searchTerm]);
  // ─── Handlers ────────────────────────────────────────────────────────────
  const handleView = (item: any) => {
    setSelectedItem(item);
    setIsViewOpen(true);
  };
  const handleEdit = (item: any) => {
    setSelectedItem(item);
    setFormData({
      ...item
    });
    setIsEditOpen(true);
  };
  const handleDelete = (item: any) => {
    setSelectedItem(item);
    setIsDeleteOpen(true);
  };
  const handleCreate = () => {
    setSelectedItem(null);
    setFormData({});
    setIsEditOpen(true);
  };
  const handleSave = () => {
    setSaveSuccess(true);
    setTimeout(() => {
      setSaveSuccess(false);
      setIsEditOpen(false);
      addNotification('success', 'Salvato', 'Elemento salvato con successo.');
    }, 1500);
  };
  const confirmDelete = () => {
    setDeleteSuccess(true);
    setTimeout(() => {
      setDeleteSuccess(false);
      setIsDeleteOpen(false);
      addNotification('success', 'Eliminato', 'Elemento eliminato con successo.');
    }, 1500);
  };
  const activeFilterCount = Object.keys(filters).filter(k => (filters as any)[k]).length;
  // ─── Shared Toolbar ───────────────────────────────────────────────────────
  const cols = columnsConfigMap[sectionId] || [];
  const toolbar = <Card className="mb-6" noPadding data-id="element-2434" data-mp="29videwno4izq">
      <div className="p-4 flex flex-col gap-4" data-id="element-2435" data-mp="29uh4oyu4k8l6">
        <div className="flex flex-col sm:flex-row gap-4" data-id="element-2436" data-mp="24w5uuo3txy2y">
          <div className="flex-1" data-id="element-2437" data-mp="24wp99uu9u4c8">
            <Input placeholder={`Cerca in ${config.title.toLowerCase()}...`} startContent={<Search className="h-4 w-4" data-id="element-2439" data-mp="1v6m9nbsn9s4x" />} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} fullWidth data-id="element-2438" data-mp="204xknpuoe8oq" />
          </div>
          <div className="flex gap-2" data-id="element-2440" data-mp="2nv1krn8nnqaz">
            <Button variant={showFilters ? 'solid' : 'flat'} color="secondary" onClick={() => setShowFilters(!showFilters)} startContent={<Filter className="h-4 w-4" data-id="element-2442" data-mp="3047srsqmp2cl" />} data-id="element-2441" data-mp="2ntzz6ou0jlf0">
              Filtri
              {activeFilterCount > 0 && <Badge variant="primary" size="sm" className="ml-1" data-id="element-2443" data-mp="352j0nxxd52s6">
                  {activeFilterCount}
                </Badge>}
            </Button>
            <ColumnCustomizer columns={cols} visibleColumns={visibleColumns} onColumnChange={setVisibleColumns} pageKey={`client-${sectionId}`} lockedColumns={getLockedColumns(sectionId)} data-id="element-2444" data-mp="3e5bpzvmzj4fd" />
            <Button variant="flat" color="secondary" startContent={<Download className="h-4 w-4" data-id="element-2446" data-mp="3svj4y1dsa5bb" />} data-id="element-2445" data-mp="3stoqn5x0pbg7">
              Esporta
            </Button>
          </div>
        </div>

        {showFilters && <div className="pt-4 border-t border-slate-100 animate-in fade-in slide-in-from-top-2" data-id="element-2447" data-mp="5pibtng4m51h">
            <SectionFilters sectionId={sectionId} filters={filters} setFilter={setFilter} data-id="element-2448" data-mp="qycheb2x0k49" />
            <div className="flex justify-end mt-4" data-id="element-2449" data-mp="10y3ac8btur28">
              <Button variant="flat" color="danger" size="sm" onClick={clearFilters} startContent={<X className="h-4 w-4" data-id="element-2451" data-mp="88wghmnbipnb" />} data-id="element-2450" data-mp="10xl7gsuntelc">
                Resetta filtri
              </Button>
            </div>
          </div>}
      </div>
    </Card>;
  // ─── Page Header ──────────────────────────────────────────────────────────
  const header = <>
      <div className="mb-6" data-id="element-2452" data-mp="d8sl9o3ual3x">
        <button onClick={onBack} className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-seform-navy transition-colors group" data-id="element-2453" data-mp="d7hgpm2kl9br">
          <ArrowLeft className="h-4 w-4 group-hover:-translate-x-1 transition-transform" data-id="element-2454" data-mp="3gpr8u86horn5" />
          <span data-id="element-2455" data-mp="1w7gqghstj5mi">{clientName}</span>
        </button>
      </div>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4" data-id="element-2456" data-mp="1w5vvws6i7mon">
        <div data-id="element-2457" data-mp="21002397y3qoo">
          <div className="flex items-center gap-3 mb-1" data-id="element-2458" data-mp="20zrmvaoco9ab">
            <Icon className="h-6 w-6 text-seform-navy" data-id="element-2459" data-mp="1n7tq7zbts5v1" />
            <h1 className="text-3xl font-bold text-seform-navy" data-id="element-2460" data-mp="1r7uai0llvba0">
              {config.title}
            </h1>
          </div>
          <p className="text-slate-500 mt-1" data-id="element-2461" data-mp="2eyrduozc509n">{config.description}</p>
        </div>
        <div className="flex items-center gap-3" data-id="element-2462" data-mp="2jxb0yspo0i3l">
          {sectionId === 'deadlines' && <div className="bg-slate-100 p-1 rounded-xl flex items-center" data-id="element-2463" data-mp="25xjfk6wtjzfk">
              <button onClick={() => setViewMode('calendar')} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${viewMode === 'calendar' ? 'bg-white text-seform-navy shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-2464" data-mp="2aw33udyvxnb3">
                <CalendarIcon className="h-4 w-4 inline-block mr-1.5" data-id="element-2465" data-mp="zel8a1rgjtzn" />{' '}
                Calendario
              </button>
              <button onClick={() => setViewMode('list')} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${viewMode === 'list' ? 'bg-white text-seform-navy shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-2466" data-mp="o0o44mbfso6w">
                <List className="h-4 w-4 inline-block mr-1.5" data-id="element-2467" data-mp="120pecsdld7z1" /> Lista
              </button>
            </div>}
          {sectionId === 'workers' && <>
              <Button variant="flat" color="secondary" startContent={<FileSpreadsheet className="h-4 w-4" data-id="element-2469" data-mp="s4lzclxn5euo" />} onClick={() => setShowTrainingImport(true)} data-id="element-2468" data-mp="16xpk126j48pm">
                Import formazione
              </Button>
              <Button variant="flat" color="secondary" startContent={<GraduationCap className="h-4 w-4" data-id="element-2471" data-mp="3ht4uez50n5r8" />} onClick={() => setShowAssignTraining(true)} data-id="element-2470" data-mp="3crff37arg5xc">
                Associa formazione
              </Button>
            </>}
          <Button onClick={handleCreate} startContent={<Plus className="h-4 w-4" data-id="element-2473" data-mp="28tlfv6r28eao" />} data-id="element-2472" data-mp="1ux84mfd4d94n">
            {config.newLabel}
          </Button>
        </div>
      </div>
    </>;
  // ─── Action Buttons (shared) ──────────────────────────────────────────────
  const actionButtons = (item: any, label: string) => <div className="flex items-center gap-1" data-id="element-2474" data-mp="1c5f7q9uxj3rn">
      <Tooltip content={`Visualizza ${label}`} data-id="element-2475" data-mp="1c6gsx75nt6n3">
        <Button variant="light" size="sm" className="!px-1" onClick={() => handleView(item)} data-id="element-2476" data-mp="1pwk8xgdy4443">
          <Eye className="h-4 w-4 text-slate-500" data-id="element-2477" data-mp="1l40xoq2qdl2g" />
        </Button>
      </Tooltip>
      <Tooltip content={`Modifica ${label}`} data-id="element-2478" data-mp="xeeyw3q9t7uz">
        <Button variant="light" size="sm" className="!px-1" onClick={() => handleEdit(item)} data-id="element-2479" data-mp="te4idx4uqz1v">
          <Edit className="h-4 w-4 text-seform-orange" data-id="element-2480" data-mp="2si4o3uacm4n5" />
        </Button>
      </Tooltip>
      <Tooltip content={`Elimina ${label}`} data-id="element-2481" data-mp="2xh7lqto018l3">
        <Button variant="light" size="sm" className="!px-1" onClick={() => handleDelete(item)} data-id="element-2482" data-mp="2xfwe2irfngop">
          <Trash2 className="h-4 w-4 text-red-500" data-id="element-2483" data-mp="37824vp5y7s2b" />
        </Button>
      </Tooltip>
    </div>;
  // ─── Workers Section ──────────────────────────────────────────────────────
  if (sectionId === 'workers') {
    return <Layout data-id="element-2484" data-mp="2ejwn9ba6yj6p">
        {header}
        {toolbar}
        <Card noPadding className="overflow-hidden" data-id="element-2485" data-mp="2jihkr2y26org">
          <div className="overflow-x-auto" data-id="element-2486" data-mp="2jgn0zryix2oy">
            <table className="w-full text-left border-collapse table-auto" data-id="element-2487" data-mp="2oh19fb41eeni">
              <thead data-id="element-2488" data-mp="2oey8hw1cj140">
                <tr className="bg-slate-50/50 border-b border-slate-100 text-xs uppercase tracking-wider text-slate-500 font-semibold" data-id="element-2489" data-mp="3uxn0b7jaghsh">
                  {cols.filter(c => visibleColumns.includes(c.key)).map(col => <th key={col.key} className="px-4 py-4" data-id="element-2490" data-mp="1ob774czepvss">
                        {col.label}
                      </th>)}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-2491" data-mp="1ehgnble869jf">
                {paginatedData.map(item => <tr key={item.id} className="hover:bg-slate-50/80 transition-colors group" data-id="element-2492" data-mp="19i4215305aiy">
                    {visibleColumns.includes('name') && <td className="px-4 py-4 text-sm font-medium text-slate-900" data-id="element-2493" data-mp="10lwejis7n7pq">
                        {item.name}
                      </td>}
                    {visibleColumns.includes('role') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-2494" data-mp="2pyzvztakr4pd">
                        {item.role}
                      </td>}
                    {visibleColumns.includes('trainingStatus') && <td className="px-4 py-4" data-id="element-2495" data-mp="1inaaoyo2etwa">
                        <Badge variant={item.trainingStatus === 'valid' ? 'success' : item.trainingStatus === 'expiring' ? 'warning' : 'danger'} data-id="element-2496" data-mp="1mnkdk2gk946x">
                          {item.trainingStatus === 'valid' ? 'In regola' : item.trainingStatus === 'expiring' ? 'In scadenza' : 'Scaduto'}
                        </Badge>
                      </td>}
                    {visibleColumns.includes('healthStatus') && <td className="px-4 py-4" data-id="element-2497" data-mp="g1705jzi701q">
                        <Badge variant="success" data-id="element-2498" data-mp="kygp6yizd9p1">Idoneo</Badge>
                      </td>}
                    {visibleColumns.includes('nextExpiry') && <td className="px-4 py-4 text-sm font-mono text-slate-600" data-id="element-2499" data-mp="3tghb4zptl4w3">
                        {item.nextExpiry}
                      </td>}
                    {visibleColumns.includes('actions') && <td className="px-4 py-4 pr-6" data-id="element-2500" data-mp="6agpf6uhs6wa">
                        {actionButtons(item, 'lavoratore')}
                      </td>}
                  </tr>)}
              </tbody>
            </table>
          </div>
          <div className="border-t border-slate-100" data-id="element-2501" data-mp="u12sejpn30zf">
            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={goToPage} totalItems={totalItems} pageSize={pageSize} onPageSizeChange={setPageSize} data-id="element-2502" data-mp="u0kqb6gapsjl" />
          </div>
        </Card>

        <AssignTrainingModal isOpen={showAssignTraining} onClose={() => setShowAssignTraining(false)} workers={sectionData.filter((w: any) => w.clientId !== undefined)} data-id="element-2503" data-mp="z1qldaxfhrwa" />
        <TrainingImportModal isOpen={showTrainingImport} onClose={() => setShowTrainingImport(false)} workers={sectionData.filter((w: any) => w.clientId !== undefined)} data-id="element-2504" data-mp="1b2q5ogiuyfz4" />

        <Modal isOpen={isViewOpen} onClose={() => setIsViewOpen(false)} title="Scheda lavoratore" size="2xl" data-id="element-2505" data-mp="1kucs790nc1h6">
          {selectedItem && <div className="space-y-6" data-id="element-2506" data-mp="1tr4ztcoh2a45">
              <WorkerDetailHeader worker={{
            ...selectedItem,
            company: selectedItem.company || clientName
          }} data-id="element-2507" data-mp="1ysau3exs65fs" />
              <div className="max-h-[55vh] overflow-y-auto custom-scrollbar pt-1 px-1" data-id="element-2508" data-mp="4e9simauj4c0">
                <WorkerAnagraficaSections worker={{
              ...selectedItem,
              company: selectedItem.company || clientName
            }} data-id="element-2509" data-mp="9d394nlfjbge" />
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-2510" data-mp="3t7zomsqcdu2x">
                <Button variant="flat" color="default" onClick={() => setIsViewOpen(false)} data-id="element-2511" data-mp="3o8n3qdj16x2z">
                  Chiudi
                </Button>
                <Button onClick={() => {
              setIsViewOpen(false);
              handleEdit(selectedItem);
            }} startContent={<Edit className="h-4 w-4" data-id="element-2513" data-mp="au0c2cwvy6ox" />} data-id="element-2512" data-mp="ksptk4kvdp4n">
                  Modifica
                </Button>
              </div>
            </div>}
        </Modal>

        <Modal isOpen={isEditOpen} onClose={() => setIsEditOpen(false)} title={selectedItem ? 'Modifica lavoratore' : 'Nuovo lavoratore'} size="2xl" data-id="element-2514" data-mp="5xjnxiporsbu">
          <AnimatePresence mode="wait" data-id="element-2515" data-mp="3g9jrwzehma6u">
            {saveSuccess ? <SaveSuccessView label="Lavoratore salvato con successo" data-id="element-2516" data-mp="3g88ncxd7wyeo" /> : <motion.div key="form" initial={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} data-id="element-2517" data-mp="3la7ri9gs7zco">
                <div className="space-y-6" data-id="element-2518" data-mp="3bcjmmqpgpbgs">
                  <WorkerTabs active={workerFormTab} onChange={setWorkerFormTab} data-id="element-2519" data-mp="2udoy0x75tnq" />
                  <div className={workerFormTab === 'info' ? '' : 'hidden'} data-id="element-2520" data-mp="7syqc3o6qjdr">
                    <WorkerAnagraficaForm value={{
                  ...formData,
                  ruolo: formData.ruolo ?? getWorkerRuolo(formData)
                }} onChange={changes => setFormData((prev: any) => ({
                  ...prev,
                  ...changes
                }))} data-id="element-2521" data-mp="3q6y7kwsl6uy3" />
                  </div>
                  <div className={workerFormTab === 'training' ? '' : 'hidden'} data-id="element-2522" data-mp="2b9c36od6aujl">
                    <WorkerTrainingTab key={selectedItem ? `edit-${selectedItem.id}` : 'new'} workerId={selectedItem?.clientId !== undefined ? selectedItem.id : undefined} initialCourses={[]} workerName={formData.name} data-id="element-2523" data-mp="26babjf1olmwi" />
                  </div>
                  <div className={workerFormTab === 'health' ? '' : 'hidden'} data-id="element-2524" data-mp="1ndhid4gv0rcb">
                    <WorkerHealthFields nextVisitDate={formData.nextVisitDate} notes={formData.notes} onChange={changes => setFormData((prev: any) => ({
                  ...prev,
                  ...changes
                }))} data-id="element-2525" data-mp="229p1s2kg1oqj" />
                  </div>
                  <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-2526" data-mp="38p96ayd8biyh">
                    <Button variant="flat" color="default" onClick={() => setIsEditOpen(false)} data-id="element-2527" data-mp="3ndn5eflf1zi7">
                      Annulla
                    </Button>
                    <Button onClick={handleSave} startContent={<Save className="h-4 w-4" data-id="element-2529" data-mp="39enhxb9n3qnm" />} data-id="element-2528" data-mp="3dgijmgvhg61x">
                      Salva lavoratore
                    </Button>
                  </div>
                </div>
              </motion.div>}
          </AnimatePresence>
        </Modal>

        <DeleteModal isOpen={isDeleteOpen} onClose={() => setIsDeleteOpen(false)} onConfirm={confirmDelete} itemLabel={selectedItem?.name || ''} deleteSuccess={deleteSuccess} data-id="element-2530" data-mp="6ujszpgkhmb" />
      </Layout>;
  }
  // ─── Deadlines Section ────────────────────────────────────────────────────
  if (sectionId === 'deadlines') {
    return <Layout data-id="element-2531" data-mp="26kruy37m90t6">
        {header}
        {viewMode === 'list' ? <>
            {toolbar}
            <Card noPadding className="overflow-hidden" data-id="element-2532" data-mp="2bijk4pgvm8to">
              <div className="overflow-x-auto" data-id="element-2533" data-mp="2bl61fgtkk2p3">
                <table className="w-full text-left border-collapse table-auto" data-id="element-2534" data-mp="2fjvbazfef23z">
                  <thead data-id="element-2535" data-mp="2kkj1xjzptgxo">
                    <tr className="bg-slate-50/50 border-b border-slate-100 text-xs uppercase tracking-wider text-slate-500 font-semibold" data-id="element-2536" data-mp="2kksthc0erw5h">
                      {cols.filter(c => visibleColumns.includes(c.key)).map(col => <th key={col.key} className="px-4 py-4" data-id="element-2537" data-mp="2z8nox05swem9">
                            {col.label}
                          </th>)}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100" data-id="element-2538" data-mp="38awvjexy7nud">
                    {paginatedData.map(item => <tr key={item.id} className="hover:bg-slate-50/80 transition-colors group" data-id="element-2539" data-mp="3d96u7zivt6kq">
                        {visibleColumns.includes('code') && <td className="px-4 py-4 font-mono text-xs text-slate-500" data-id="element-2540" data-mp="1mw8o9k3z7iit">
                            {item.code}
                          </td>}
                        {visibleColumns.includes('description') && <td className="px-4 py-4 text-sm font-medium text-slate-900" data-id="element-2541" data-mp="1vvm18mrb6p8a">
                            {item.description}
                          </td>}
                        {visibleColumns.includes('type') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-2542" data-mp="20w9oqygbwnxr">
                            {item.type}
                          </td>}
                        {visibleColumns.includes('date') && <td className="px-4 py-4 text-sm font-mono text-slate-600" data-id="element-2543" data-mp="6jadfhizefwa">
                            {item.date}
                          </td>}
                        {visibleColumns.includes('notifyClient') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-2544" data-mp="28fiw6m59yck6">
                            {item.notifyClient}
                          </td>}
                        {visibleColumns.includes('responsible') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-2545" data-mp="1ze1gv5zoyewj">
                            {item.responsible}
                          </td>}
                        {visibleColumns.includes('status') && <td className="px-4 py-4 whitespace-nowrap" data-id="element-2546" data-mp="1ufrpl6h37yg7">
                            <Badge variant={item.status === 'Urgente' ? 'danger' : item.status === 'In scadenza' ? 'warning' : item.status === 'Completata' ? 'success' : 'info'} data-id="element-2547" data-mp="1ug01av1h4xpt">
                              {item.status}
                            </Badge>
                          </td>}
                        {visibleColumns.includes('actions') && <td className="px-4 py-4 pr-6" data-id="element-2548" data-mp="15ui0vui31ahs">
                            {actionButtons(item, 'scadenza')}
                          </td>}
                      </tr>)}
                  </tbody>
                </table>
              </div>
              <div className="border-t border-slate-100" data-id="element-2549" data-mp="rzfpirugusxl">
                <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={goToPage} totalItems={totalItems} pageSize={pageSize} onPageSizeChange={setPageSize} data-id="element-2550" data-mp="n0uo4p3ha37k" />
              </div>
            </Card>
          </> : <CalendarView events={searchResult.map((item: any) => ({
        id: String(item.id),
        title: item.description,
        date: item.date,
        type: item.type,
        priority: item.status === 'Urgente' ? 'high' : item.status === 'In scadenza' ? 'medium' : 'low',
        client: clientName
      }))} onEventClick={(event: any) => {
        const found = mockDeadlines.find(d => String(d.id) === event.id);
        if (found) handleView(found);
      }} data-id="element-2551" data-mp="hxljk4rco3f6" />}

        <Modal isOpen={isViewOpen} onClose={() => setIsViewOpen(false)} title="Dettaglio scadenza" size="lg" data-id="element-2552" data-mp="32lqc1ffw7cl3">
          {selectedItem && <div className="space-y-6" data-id="element-2553" data-mp="2zjv943dw8177">
              <div className="grid grid-cols-2 gap-4" data-id="element-2554" data-mp="2uneo3i1zq2yc">
                <div data-id="element-2555" data-mp="2ukshplqu5thp">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2556" data-mp="39brjact97hrs">
                    Codice
                  </span>
                  <p className="font-mono font-medium mt-1" data-id="element-2557" data-mp="34dpst6ti0g6a">
                    {selectedItem.code}
                  </p>
                </div>
                <div data-id="element-2558" data-mp="2gp6loum6q9fs">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2559" data-mp="2bo916bdpd3b4">
                    Stato
                  </span>
                  <div className="mt-1" data-id="element-2560" data-mp="2qmbal27f0j0a">
                    <Badge variant={selectedItem.status === 'Urgente' ? 'danger' : selectedItem.status === 'In scadenza' ? 'warning' : 'info'} data-id="element-2561" data-mp="2qix53l3gftom">
                      {selectedItem.status}
                    </Badge>
                  </div>
                </div>
                <div className="col-span-full" data-id="element-2562" data-mp="dfcziv6o15c2">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2563" data-mp="3vk6gyyz27m9y">
                    Descrizione
                  </span>
                  <p className="font-medium mt-1" data-id="element-2564" data-mp="3hfa44d2l2hv">{selectedItem.description}</p>
                </div>
                <div data-id="element-2565" data-mp="r6zwkx8fmhvw">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2566" data-mp="r6qgq2h3pr3x">
                    Tipologia
                  </span>
                  <p className="font-medium mt-1" data-id="element-2567" data-mp="idwm8xt32l5h">{selectedItem.type}</p>
                </div>
                <div data-id="element-2568" data-mp="mf8df7rlvefs">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2569" data-mp="meqcvyxwue22">
                    Data scadenza
                  </span>
                  <p className="font-mono font-medium mt-1" data-id="element-2570" data-mp="1a3j8tvej8lwo">
                    {selectedItem.date}
                  </p>
                </div>
                <div data-id="element-2571" data-mp="q4mzy95q1cqg">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2572" data-mp="q5ybww9blqsn">
                    Responsabile
                  </span>
                  <p className="font-medium mt-1" data-id="element-2573" data-mp="lc3ll9z7mdkl">{selectedItem.responsible}</p>
                </div>
                <div data-id="element-2574" data-mp="gczdkmo0ulvu">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2575" data-mp="bg8wbw0hdk8u">
                    Notifica cliente
                  </span>
                  <p className="font-medium mt-1" data-id="element-2576" data-mp="192fxevxksugg">
                    {selectedItem.notifyClient}
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-2577" data-mp="z51wk4o4ye99">
                <Button variant="flat" color="default" onClick={() => setIsViewOpen(false)} data-id="element-2578" data-mp="u6i5xqylkeak">
                  Chiudi
                </Button>
                <Button onClick={() => {
              setIsViewOpen(false);
              handleEdit(selectedItem);
            }} startContent={<Edit className="h-4 w-4" data-id="element-2580" data-mp="1zdrjxqjilrb0" />} data-id="element-2579" data-mp="3ew7nwculkg7h">
                  Modifica
                </Button>
              </div>
            </div>}
        </Modal>

        <Modal isOpen={isEditOpen} onClose={() => setIsEditOpen(false)} title={selectedItem ? 'Modifica scadenza' : 'Nuova scadenza'} size="lg" data-id="element-2581" data-mp="24arr64s3p03p">
          <AnimatePresence mode="wait" data-id="element-2582" data-mp="2e4qomqh8bvpb">
            {saveSuccess ? <SaveSuccessView label="Scadenza salvata con successo" data-id="element-2583" data-mp="2i2y3tgw1ooye" /> : <motion.div key="form" initial={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} data-id="element-2584" data-mp="2n4dsrk1cffm2">
                <div className="space-y-6" data-id="element-2585" data-mp="sqw1pn1qc0ma">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2586" data-mp="xo5y5mnjdcjm">
                    <Input label="Codice" value={formData.code || ''} onChange={e => setFormData({
                  ...formData,
                  code: e.target.value
                })} data-id="element-2587" data-mp="xqifpypdtkw5" />
                    <Select label="Tipologia" value={formData.type || ''} onChange={e => setFormData({
                  ...formData,
                  type: e.target.value
                })} options={[{
                  value: 'Riunione annuale',
                  label: 'Riunione annuale'
                }, {
                  value: 'Prova di emergenza',
                  label: 'Prova di emergenza'
                }, {
                  value: 'Tampone alimentare',
                  label: 'Tampone alimentare'
                }, {
                  value: 'Campionamento alimentare',
                  label: 'Campionamento alimentare'
                }, {
                  value: 'Audit interno',
                  label: 'Audit interno'
                }, {
                  value: 'Report annuale',
                  label: 'Report annuale'
                }]} required data-id="element-2588" data-mp="3fk4ffd4plwoq" />
                    <div className="col-span-full" data-id="element-2589" data-mp="21jhomr27onba">
                      <Textarea label="Descrizione" value={formData.description || ''} onChange={e => setFormData({
                    ...formData,
                    description: e.target.value
                  })} required data-id="element-2590" data-mp="v47ctqluvecq" />
                    </div>
                    <DatePicker label="Data scadenza" value={formData.date || ''} onChange={e => setFormData({
                  ...formData,
                  date: e.target.value
                })} data-id="element-2591" data-mp="iktjtyh5a4t8" />
                    <Input label="Responsabile" value={formData.responsible || ''} onChange={e => setFormData({
                  ...formData,
                  responsible: e.target.value
                })} data-id="element-2592" data-mp="3v3iawmqyb0ql" />
                    <Select label="Stato" value={formData.status || ''} onChange={e => setFormData({
                  ...formData,
                  status: e.target.value
                })} options={[{
                  value: 'Pianificata',
                  label: 'Pianificata'
                }, {
                  value: 'In scadenza',
                  label: 'In scadenza'
                }, {
                  value: 'Urgente',
                  label: 'Urgente'
                }, {
                  value: 'Completata',
                  label: 'Completata'
                }]} required data-id="element-2593" data-mp="3r37ueg5j8rxh" />
                    <div className="flex items-center gap-2 mt-2" data-id="element-2594" data-mp="22h832odjr2yi">
                      <input type="checkbox" id="notifyClientEdit" className="rounded text-seform-orange focus:ring-seform-orange" checked={formData.notifyClient === 'Sì'} onChange={e => setFormData({
                    ...formData,
                    notifyClient: e.target.checked ? 'Sì' : 'No'
                  })} data-id="element-2595" data-mp="22evl4b7sgslg" />
                      <label htmlFor="notifyClientEdit" className="text-sm text-slate-700" data-id="element-2596" data-mp="1si8zvlbjvziw">
                        Notifica cliente
                      </label>
                    </div>
                  </div>
                  <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-2597" data-mp="38w8zswaebp65">
                    <Button variant="flat" color="default" onClick={() => setIsEditOpen(false)} data-id="element-2598" data-mp="3j0fnpwj9sp1m">
                      Annulla
                    </Button>
                    <Button onClick={handleSave} startContent={<Save className="h-4 w-4" data-id="element-2600" data-mp="35897ybk14k2p" />} data-id="element-2599" data-mp="399kxkh2wl5aw">
                      Salva scadenza
                    </Button>
                  </div>
                </div>
              </motion.div>}
          </AnimatePresence>
        </Modal>

        <DeleteModal isOpen={isDeleteOpen} onClose={() => setIsDeleteOpen(false)} onConfirm={confirmDelete} itemLabel={selectedItem?.description || ''} deleteSuccess={deleteSuccess} data-id="element-2601" data-mp="2qc1kn2dbr2j7" />
      </Layout>;
  }
  // ─── Contracts Section ────────────────────────────────────────────────────
  if (sectionId === 'contracts') {
    return <Layout data-id="element-2602" data-mp="2e1shgcqzx1vv">
        {header}
        {toolbar}
        <Card noPadding className="overflow-hidden" data-id="element-2603" data-mp="2e10m931b9664">
          <div className="overflow-x-auto" data-id="element-2604" data-mp="2is8uvwutgni8">
            <table className="w-full text-left border-collapse table-auto" data-id="element-2605" data-mp="2itaif0t0r2gu">
              <thead data-id="element-2606" data-mp="251x2rxy8eor3">
                <tr className="bg-slate-50/50 border-b border-slate-100 text-xs uppercase tracking-wider text-slate-500 font-semibold" data-id="element-2607" data-mp="252f9joiisld9">
                  {cols.filter(c => visibleColumns.includes(c.key)).map(col => <th key={col.key} className="px-4 py-4" data-id="element-2608" data-mp="31rwjwgsg6vla">
                        {col.label}
                      </th>)}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-2609" data-mp="2sqgbgel713jp">
                {paginatedData.map(item => <tr key={item.id} className="hover:bg-slate-50/80 transition-colors group" data-id="element-2610" data-mp="1cbx4wj25ebxq">
                    {visibleColumns.includes('code') && <td className="px-4 py-4 font-mono text-xs text-slate-500" data-id="element-2611" data-mp="18thjfj733s1k">
                        {item.code}
                      </td>}
                    {visibleColumns.includes('object') && <td className="px-4 py-4 text-sm font-medium text-slate-900" data-id="element-2612" data-mp="1irxc3qlrf9d6">
                        {item.object}
                      </td>}
                    {visibleColumns.includes('type') && <td className="px-4 py-4" data-id="element-2613" data-mp="l4ybx5vuzjaj">
                        <Badge variant={item.type === 'Contratto' ? 'primary' : 'neutral'} data-id="element-2614" data-mp="l69e519noizj">
                          {item.type}
                        </Badge>
                      </td>}
                    {visibleColumns.includes('value') && <td className="px-4 py-4 text-sm font-medium text-slate-900" data-id="element-2615" data-mp="2f8iam3u13k7">
                        {item.value}
                      </td>}
                    {visibleColumns.includes('status') && <td className="px-4 py-4" data-id="element-2616" data-mp="3i04og64u3xxj">
                        <Badge variant={item.status === 'Attivo' ? 'success' : item.status === 'In attesa' ? 'warning' : item.status === 'Scaduto' ? 'danger' : 'neutral'} data-id="element-2617" data-mp="3myz634r2fgg2">
                          {item.status}
                        </Badge>
                      </td>}
                    {visibleColumns.includes('date') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-2618" data-mp="jjk0cfppzt0q">
                        {item.date}
                      </td>}
                    {visibleColumns.includes('actions') && <td className="px-4 py-4 pr-6" data-id="element-2619" data-mp="27zigri0drnxh">
                        {actionButtons(item, 'contratto')}
                      </td>}
                  </tr>)}
              </tbody>
            </table>
          </div>
          <div className="border-t border-slate-100" data-id="element-2620" data-mp="1u2vinbwoyrpz">
            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={goToPage} totalItems={totalItems} pageSize={pageSize} onPageSizeChange={setPageSize} data-id="element-2621" data-mp="1u46m1am85xgk" />
          </div>
        </Card>

        <Modal isOpen={isViewOpen} onClose={() => setIsViewOpen(false)} title="Dettaglio contratto" size="2xl" data-id="element-2622" data-mp="2hxg5s7q2dor5">
          {selectedItem && <div className="space-y-8" data-id="element-2623" data-mp="3ahwsgo0tgxtd">
              <section data-id="element-2624" data-mp="35p3klra1x9d2">
                <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-2625" data-mp="35nsjjz7zqfpn">
                  <FileText className="h-4 w-4" data-id="element-2626" data-mp="baz016lw4yl8" /> Dati generali
                </h4>
                <div className="grid grid-cols-2 gap-4" data-id="element-2627" data-mp="6bmd6lv0rrin">
                  <div data-id="element-2628" data-mp="ka2lgh8j5q2w">
                    <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2629" data-mp="kac93y63rl5f">
                      Codice
                    </span>
                    <p className="font-mono font-medium mt-1" data-id="element-2630" data-mp="fcsu2w1kz4cf">
                      {selectedItem.code}
                    </p>
                  </div>
                  <div data-id="element-2631" data-mp="p7uljyjbwii6">
                    <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2632" data-mp="p8duipr03wk2">
                      Cliente
                    </span>
                    <p className="font-medium mt-1" data-id="element-2633" data-mp="yxxclt6w3mdv">{clientName}</p>
                  </div>
                  <div data-id="element-2634" data-mp="1cxyos4slug8n">
                    <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2635" data-mp="1cxf3ce3qx9pv">
                      Prodotto
                    </span>
                    <p className="font-medium mt-1" data-id="element-2636" data-mp="17x0xn2pmabv0">{selectedItem.object}</p>
                  </div>
                  <div data-id="element-2637" data-mp="1riucye1atl0p">
                    <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2638" data-mp="1wjqh8spkb96n">
                      Tipologia
                    </span>
                    <p className="font-medium mt-1" data-id="element-2639" data-mp="1hly3a4znysuk">
                      {selectedItem.tipologia || 'Sicurezza'}
                    </p>
                  </div>
                  <div data-id="element-2640" data-mp="18tw56dd2hong">
                    <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2641" data-mp="18tea3k255gh4">
                      Responsabile sviluppo
                    </span>
                    <p className="font-medium mt-1" data-id="element-2642" data-mp="yw9p3nk17r1w">
                      {selectedItem.responsabileSviluppo || 'Alessio'}
                    </p>
                  </div>
                  <div data-id="element-2643" data-mp="13vm8fybp2fzq">
                    <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2644" data-mp="pvju2m2li4p8">
                      Provenienza
                    </span>
                    <p className="font-medium mt-1" data-id="element-2645" data-mp="tvvdjvj81yz4">
                      {selectedItem.provenienza || 'Commerciale SEF'}
                    </p>
                  </div>
                  <div data-id="element-2646" data-mp="2rslp176m9j2n">
                    <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2647" data-mp="2rs2g2fyy250r">
                      Corrispettivo provenienza
                    </span>
                    <p className="font-mono font-medium mt-1" data-id="element-2648" data-mp="2huxqe65w3rf7">
                      {selectedItem.corrispettivoProvenienza || '€ 500'}
                    </p>
                  </div>
                </div>
              </section>

              <div className="h-px bg-slate-100" data-id="element-2649" data-mp="3ajbp6o4syrrt"></div>

              <section data-id="element-2650" data-mp="3ahscf7icuyus">
                <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-2651" data-mp="35jz68eh1y4ui">
                  <AlertTriangle className="h-4 w-4" data-id="element-2652" data-mp="31hviorh3z9sv" /> Stato e workflow
                </h4>
                <div className="grid grid-cols-2 gap-4 mb-4" data-id="element-2653" data-mp="1u5ljjcy1bvg2">
                  <div data-id="element-2654" data-mp="1q3rnjhysbfm8">
                    <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2655" data-mp="1q38ekqr441kc">
                      Stato attuale
                    </span>
                    <div className="mt-1" data-id="element-2656" data-mp="5nk2yxsicr0v">
                      <Badge variant={selectedItem.status === 'Attivo' || selectedItem.status === 'accettato' ? 'success' : selectedItem.status === 'In attesa' || selectedItem.status === 'in_valutazione' ? 'warning' : selectedItem.status === 'Scaduto' || selectedItem.status === 'non_accettato' || selectedItem.status === 'revocato' ? 'danger' : 'neutral'} data-id="element-2657" data-mp="9msxw5zaauy0">
                        {selectedItem.status}
                      </Badge>
                    </div>
                  </div>
                  <div data-id="element-2658" data-mp="28swnrlv6awzt">
                    <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2659" data-mp="28sdgcz35fr01">
                      Data cambio stato
                    </span>
                    <p className="font-medium mt-1" data-id="element-2660" data-mp="23wfzmrnz4imq">
                      {selectedItem.dataCambioStato || selectedItem.date || '15/03/2025'}
                    </p>
                  </div>
                </div>

                {/* Conditional status-specific fields */}
                {['Attivo', 'accettato'].includes(selectedItem.status) && <div className="bg-green-50 p-4 rounded-lg border border-green-100" data-id="element-2661" data-mp="1l1hk9prnu9mz">
                    <h4 className="text-sm font-bold text-green-800 uppercase tracking-wider mb-3" data-id="element-2662" data-mp="1ztt6mnfkgrny">
                      Dettagli accettazione
                    </h4>
                    <div className="grid grid-cols-2 gap-4" data-id="element-2663" data-mp="17b5q7lbim0t7">
                      <div data-id="element-2664" data-mp="12dci2mqniwqa">
                        <span className="text-xs font-semibold text-green-600 uppercase" data-id="element-2665" data-mp="12c1fgq8xzv0r">
                          Data di accettazione
                        </span>
                        <p className="font-medium text-green-900" data-id="element-2666" data-mp="3iqwe5imxpd4g">
                          {selectedItem.acceptanceDate || '15/03/2025'}
                        </p>
                      </div>
                    </div>
                  </div>}

                {['non_accettato', 'rejected'].includes(selectedItem.status) && <div className="bg-red-50 p-4 rounded-lg border border-red-100" data-id="element-2667" data-mp="jqi9bnzr3y9">
                    <h4 className="text-sm font-bold text-red-800 uppercase tracking-wider mb-3" data-id="element-2668" data-mp="5juxyzh2runf">
                      Dettagli non accettazione
                    </h4>
                    <div className="grid grid-cols-2 gap-4" data-id="element-2669" data-mp="3rqt55qwsqo2o">
                      <div data-id="element-2670" data-mp="2cbca0ny29kx1">
                        <span className="text-xs font-semibold text-red-600 uppercase" data-id="element-2671" data-mp="2ca152ksvq74c">
                          Data non accettazione
                        </span>
                        <p className="font-medium text-red-900" data-id="element-2672" data-mp="mtp56dxfktuc">
                          {selectedItem.rejectionDate || '20/02/2025'}
                        </p>
                      </div>
                      <div data-id="element-2673" data-mp="d45mb89pwzzh">
                        <span className="text-xs font-semibold text-red-600 uppercase" data-id="element-2674" data-mp="d3dpju4dww7m">
                          Motivazione
                        </span>
                        <p className="font-medium text-red-900" data-id="element-2675" data-mp="8384wxwj89z1">
                          {selectedItem.rejectionReason || 'Budget insufficiente'}
                        </p>
                      </div>
                    </div>
                  </div>}

                {['revocato', 'revoked'].includes(selectedItem.status) && <div className="bg-orange-50 p-4 rounded-lg border border-orange-100" data-id="element-2676" data-mp="3ld5i936f0d3g">
                    <h4 className="text-sm font-bold text-orange-800 uppercase tracking-wider mb-3" data-id="element-2677" data-mp="1oi130lg9tgst">
                      Dettagli revoca
                    </h4>
                    <div className="grid grid-cols-2 gap-4" data-id="element-2678" data-mp="1s1rorb5kmqbc">
                      <div data-id="element-2679" data-mp="1rz5hlcmlectn">
                        <span className="text-xs font-semibold text-orange-600 uppercase" data-id="element-2680" data-mp="1w2lfecaeej7x">
                          Data revoca
                        </span>
                        <p className="font-medium text-orange-900" data-id="element-2681" data-mp="20ubq4jd8z8zv">
                          {selectedItem.revokeDate || '10/01/2025'}
                        </p>
                      </div>
                      <div data-id="element-2682" data-mp="193yoyhq8uwi6">
                        <span className="text-xs font-semibold text-orange-600 uppercase" data-id="element-2683" data-mp="194qfhe4zi41l">
                          Motivazione
                        </span>
                        <p className="font-medium text-orange-900" data-id="element-2684" data-mp="1i2ahwqel26ds">
                          {selectedItem.revokeReason || 'Cambio fornitore'}
                        </p>
                      </div>
                    </div>
                  </div>}
              </section>

              <div className="h-px bg-slate-100" data-id="element-2685" data-mp="2yg91lwt7me2b"></div>

              <section data-id="element-2686" data-mp="3jlcmqu0nww0c">
                <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-2687" data-mp="3jkl1od4oygob">
                  <Euro className="h-4 w-4" data-id="element-2688" data-mp="3fl2etbtb7viu" /> Dettagli economici
                </h4>
                <div className="grid grid-cols-2 gap-4" data-id="element-2689" data-mp="3tc7kovn9tk1m">
                  <div data-id="element-2690" data-mp="3okfs6fkk148u">
                    <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2691" data-mp="3ojxm6r83bbnq">
                      Importo
                    </span>
                    <p className="font-mono font-bold text-seform-navy mt-1" data-id="element-2692" data-mp="724nvhux6s2o">
                      {selectedItem.value}
                    </p>
                  </div>
                  <div data-id="element-2693" data-mp="g4w03jvmdrvd">
                    <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2694" data-mp="g4478gteq88s">
                      Durata (mesi)
                    </span>
                    <p className="font-medium mt-1" data-id="element-2695" data-mp="2hvqzsmsy6b7d">
                      {selectedItem.durata || '12'}
                    </p>
                  </div>
                  <div data-id="element-2696" data-mp="2cyh62uyizddq">
                    <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2697" data-mp="2czaf62kyiez1">
                      Data preventivo
                    </span>
                    <p className="font-medium mt-1" data-id="element-2698" data-mp="xcx09hxga4tr">
                      {selectedItem.dataPreventivo || selectedItem.date || '10/01/2025'}
                    </p>
                  </div>
                  <div data-id="element-2699" data-mp="nm2aw4owqp43">
                    <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2700" data-mp="9oniqs79s38f">
                      Scadenza
                    </span>
                    <p className="font-medium mt-1" data-id="element-2701" data-mp="eo9usybd3dfn">
                      {selectedItem.expiry || selectedItem.date}
                    </p>
                  </div>
                  <div data-id="element-2702" data-mp="4ordkpmu65ra">
                    <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2703" data-mp="4qc7qe58nmom">
                      Tacito rinnovo
                    </span>
                    <p className="font-medium mt-1" data-id="element-2704" data-mp="1uzvwxso57crn">
                      {selectedItem.tacitRenewal !== false ? 'Sì' : 'No'}
                    </p>
                  </div>
                </div>
              </section>

              <div className="flex justify-between items-center pt-4 border-t border-slate-100" data-id="element-2705" data-mp="34yr3f1fnws58">
                <div data-id="element-2706" data-mp="2qae53xt3qx7k">
                  {['Attivo', 'accettato'].includes(selectedItem.status) && <Button variant="flat" color="success" startContent={<ClipboardList className="h-4 w-4" data-id="element-2708" data-mp="2h8epn9xqk16q" />} onClick={() => setIsGenerateActivityOpen(true)} data-id="element-2707" data-mp="2v8phaf6vdjsy">
                      Genera attività operativa
                    </Button>}
                </div>
                <div className="flex gap-3" data-id="element-2709" data-mp="2cad3uh8xdnrk">
                  <Button variant="flat" color="default" onClick={() => setIsViewOpen(false)} data-id="element-2710" data-mp="2cb4sf843ul8c">
                    Chiudi
                  </Button>
                  <Button onClick={() => {
                setIsViewOpen(false);
                handleEdit(selectedItem);
              }} startContent={<Edit className="h-4 w-4" data-id="element-2712" data-mp="1q3r84x2sffb2" />} data-id="element-2711" data-mp="agw0hprtv92m">
                    Modifica
                  </Button>
                </div>
              </div>
            </div>}
        </Modal>

        <GenerateActivityWizardModal isOpen={isGenerateActivityOpen} onClose={() => setIsGenerateActivityOpen(false)} data-id="element-2713" data-mp="1zy122j7dolnw" />

        <Modal isOpen={isEditOpen} onClose={() => setIsEditOpen(false)} title={selectedItem ? 'Modifica contratto' : 'Nuovo contratto'} size="2xl" data-id="element-2714" data-mp="29qow566z4bix">
          <AnimatePresence mode="wait" data-id="element-2715" data-mp="2iqc121ywvzgq">
            {saveSuccess ? <SaveSuccessView label="Contratto salvato con successo" data-id="element-2716" data-mp="tbnkrpn79xdf" /> : <motion.div key="form" initial={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} data-id="element-2717" data-mp="tdi7n9i17zk5">
                <div className="space-y-8" data-id="element-2718" data-mp="2tdnam6brumyl">
                  <section data-id="element-2719" data-mp="2tegft2v314em">
                    <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-2720" data-mp="2tfrjz3sfwe69">
                      <FileText className="h-4 w-4" data-id="element-2721" data-mp="32f4kh6yl5seu" /> Dati generali
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2722" data-mp="37dxwsw24zddf">
                      <ProgressiveNumberInput type="contract" value={formData.code || ''} onChange={val => setFormData({
                    ...formData,
                    code: val
                  })} disabled={!!selectedItem} data-id="element-2723" data-mp="37bv9wkwa60cx" />
                      <Input label="Cliente" value={clientName} disabled data-id="element-2724" data-mp="3v2r05dl38ni2" />
                      <Input label="Prodotto / Oggetto" value={formData.object || ''} onChange={e => setFormData({
                    ...formData,
                    object: e.target.value
                  })} required data-id="element-2725" data-mp="91tc8x11v9pq" />
                      <Select label="Tipologia" value={formData.tipologia || ''} onChange={e => setFormData({
                    ...formData,
                    tipologia: e.target.value
                  })} options={[{
                    value: '',
                    label: 'Seleziona...'
                  }, {
                    value: 'Sicurezza',
                    label: 'Sicurezza'
                  }, {
                    value: 'Privacy',
                    label: 'Privacy'
                  }, {
                    value: 'HACCP',
                    label: 'HACCP'
                  }, {
                    value: 'Altro',
                    label: 'Altro'
                  }]} data-id="element-2726" data-mp="rsua1hqfoz7p" />
                      <Select label="Responsabile sviluppo" value={formData.responsabileSviluppo || ''} onChange={e => setFormData({
                    ...formData,
                    responsabileSviluppo: e.target.value
                  })} options={[{
                    value: 'alessio',
                    label: 'Alessio'
                  }, {
                    value: 'gianfranco',
                    label: 'Gian Franco'
                  }, {
                    value: 'lorenzo',
                    label: 'Lorenzo'
                  }, {
                    value: 'tiziano',
                    label: 'Tiziano'
                  }]} required data-id="element-2727" data-mp="1088hj4yan3kn" />
                      <Select label="Provenienza" value={formData.provenienza || ''} onChange={e => setFormData({
                    ...formData,
                    provenienza: e.target.value
                  })} options={[{
                    value: '',
                    label: 'Seleziona...'
                  }, {
                    value: 'Commerciale SEF',
                    label: 'Commerciale SEF'
                  }, {
                    value: 'Esterni e partner',
                    label: 'Esterni e partner'
                  }, {
                    value: 'Altro',
                    label: 'Altro'
                  }]} data-id="element-2728" data-mp="33g91q2e5abmp" />
                      <Input label="Corrispettivo provenienza (€)" placeholder="Es. 500,00" value={formData.corrispettivoProvenienza?.replace('€ ', '') || ''} onChange={e => setFormData({
                    ...formData,
                    corrispettivoProvenienza: e.target.value
                  })} data-id="element-2729" data-mp="3ki65rp08g1t5" />
                    </div>
                  </section>

                  <div className="h-px bg-slate-100" data-id="element-2730" data-mp="1iwka4wpatczy"></div>

                  <section data-id="element-2731" data-mp="1iuzfl72zhu23">
                    <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-2732" data-mp="1iupn9cugvat8">
                      <AlertTriangle className="h-4 w-4" data-id="element-2733" data-mp="19w45hjwtjzti" /> Stato e workflow
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2734" data-mp="14xatxx13eivz">
                      <Select label="Stato attuale" value={formData.status || ''} onChange={e => setFormData({
                    ...formData,
                    status: e.target.value
                  })} options={[{
                    value: 'da_proporre',
                    label: 'Da proporre'
                  }, {
                    value: 'in_valutazione',
                    label: 'In valutazione'
                  }, {
                    value: 'da_sollecitare',
                    label: 'Da sollecitare'
                  }, {
                    value: 'accettato',
                    label: 'Accettato'
                  }, {
                    value: 'non_accettato',
                    label: 'Non accettato'
                  }, {
                    value: 'revocato',
                    label: 'Revocato'
                  }, {
                    value: 'Attivo',
                    label: 'Attivo (Legacy)'
                  }, {
                    value: 'In attesa',
                    label: 'In attesa (Legacy)'
                  }, {
                    value: 'Bozza',
                    label: 'Bozza (Legacy)'
                  }, {
                    value: 'Scaduto',
                    label: 'Scaduto (Legacy)'
                  }]} required data-id="element-2735" data-mp="14ym0g4lxa4qs" />
                      <Input label="Data cambio stato" type="date" value={formData.dataCambioStato || new Date().toISOString().split('T')[0]} onChange={e => setFormData({
                    ...formData,
                    dataCambioStato: e.target.value
                  })} data-id="element-2736" data-mp="257dcpse6smhy" />

                      {['accettato', 'Attivo'].includes(formData.status) && <Input label="Data di accettazione" type="date" value={formData.acceptanceDate || ''} onChange={e => setFormData({
                    ...formData,
                    acceptanceDate: e.target.value
                  })} data-id="element-2737" data-mp="2itd6iyml40fg" />}

                      {['non_accettato', 'rejected'].includes(formData.status) && <>
                          <Input label="Data non accettazione" type="date" value={formData.rejectionDate || ''} onChange={e => setFormData({
                      ...formData,
                      rejectionDate: e.target.value
                    })} data-id="element-2738" data-mp="pdilbuveka23" />
                          <div className="col-span-full" data-id="element-2739" data-mp="afq4mze4dfmb">
                            <Input label="Motivazione" placeholder="Inserisci la motivazione..." value={formData.rejectionReason || ''} onChange={e => setFormData({
                        ...formData,
                        rejectionReason: e.target.value
                      })} data-id="element-2740" data-mp="ahazkq4cj0kp" />
                          </div>
                        </>}

                      {['revocato', 'revoked'].includes(formData.status) && <>
                          <Input label="Data revoca" type="date" value={formData.revokeDate || ''} onChange={e => setFormData({
                      ...formData,
                      revokeDate: e.target.value
                    })} data-id="element-2741" data-mp="3th8gw7mrohe3" />
                          <div className="col-span-full" data-id="element-2742" data-mp="1ioxd1ep7ws6b">
                            <Input label="Motivazione" placeholder="Inserisci la motivazione della revoca..." value={formData.revokeReason || ''} onChange={e => setFormData({
                        ...formData,
                        revokeReason: e.target.value
                      })} data-id="element-2743" data-mp="1q8ged12bx83p" />
                          </div>
                        </>}
                    </div>
                  </section>

                  <div className="h-px bg-slate-100" data-id="element-2744" data-mp="248hegearsbi4"></div>

                  <section data-id="element-2745" data-mp="246o9d3ft1zc8">
                    <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-2746" data-mp="2ry223ahvbwxq">
                      <Euro className="h-4 w-4" data-id="element-2747" data-mp="2wpbmnwok8a2r" /> Dettagli economici
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2748" data-mp="19103b6p3t7c9">
                      <Input label="Importo (€)" type="number" placeholder="0.00" value={formData.value?.replace('€ ', '') || ''} onChange={e => setFormData({
                    ...formData,
                    value: e.target.value
                  })} required data-id="element-2749" data-mp="141dpav1gbn2e" />
                      <Input label="Durata (mesi)" type="number" value={formData.durata || '12'} onChange={e => setFormData({
                    ...formData,
                    durata: e.target.value
                  })} required data-id="element-2750" data-mp="1i0vidmls5mhk" />
                      <Input label="Data preventivo" type="date" value={formData.dataPreventivo || formData.date || ''} onChange={e => setFormData({
                    ...formData,
                    dataPreventivo: e.target.value
                  })} data-id="element-2751" data-mp="2e0ye4sine6a6" />
                      <div className="flex items-center gap-2 pt-6" data-id="element-2752" data-mp="30zbaanaq4zof">
                        <input type="checkbox" id="renewal" className="rounded border-slate-300 text-seform-orange focus:ring-seform-orange" checked={formData.tacitRenewal ?? false} onChange={e => setFormData({
                      ...formData,
                      tacitRenewal: e.target.checked
                    })} data-id="element-2753" data-mp="30xgo75npv1ir" />
                        <label htmlFor="renewal" className="text-sm text-slate-700" data-id="element-2754" data-mp="2q1wleuifycc6">
                          Tacito rinnovo
                        </label>
                      </div>
                    </div>
                  </section>

                  <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-2755" data-mp="3dobi05xcoltf">
                    <Button variant="flat" color="default" onClick={() => setIsEditOpen(false)} data-id="element-2756" data-mp="38qjqhd0mb1pr">
                      Annulla
                    </Button>
                    <Button onClick={handleSave} startContent={<Save className="h-4 w-4" data-id="element-2758" data-mp="1tc5s2ylvyv7y" />} data-id="element-2757" data-mp="1tab93pu6dd6i">
                      Salva contratto
                    </Button>
                  </div>
                </div>
              </motion.div>}
          </AnimatePresence>
        </Modal>

        <DeleteModal isOpen={isDeleteOpen} onClose={() => setIsDeleteOpen(false)} onConfirm={confirmDelete} itemLabel={selectedItem?.code || ''} deleteSuccess={deleteSuccess} data-id="element-2759" data-mp="8rqx81w931vf" />
      </Layout>;
  }
  // ─── Invoices Section ─────────────────────────────────────────────────────
  if (sectionId === 'invoices') {
    return <Layout data-id="element-2760" data-mp="3laqej8uxdcbk">
        {header}
        {toolbar}
        <Card noPadding className="overflow-hidden" data-id="element-2761" data-mp="3l7ch8f14btaz">
          <div className="overflow-x-auto" data-id="element-2762" data-mp="37b7kfjyy81i9">
            <table className="w-full text-left border-collapse table-auto" data-id="element-2763" data-mp="3c8zcqf3i9pmz">
              <thead data-id="element-2764" data-mp="3c9915y8wjoqk">
                <tr className="bg-slate-50/50 border-b border-slate-100 text-xs uppercase tracking-wider text-slate-500 font-semibold" data-id="element-2765" data-mp="15ijeh2ux6zys">
                  {cols.filter(c => visibleColumns.includes(c.key)).map(col => <th key={col.key} className="px-4 py-4" data-id="element-2766" data-mp="3tfdu3fv80z11">
                        {col.label}
                      </th>)}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-2767" data-mp="3kmk8z1t3f5f9">
                {paginatedData.map(item => <tr key={item.id} className="hover:bg-slate-50/80 transition-colors group" data-id="element-2768" data-mp="3foigxrdovvrn">
                    {visibleColumns.includes('number') && <td className="px-4 py-4 font-mono text-xs text-slate-500" data-id="element-2769" data-mp="35sp92lax0qg7">
                        {item.number}
                      </td>}
                    {visibleColumns.includes('description') && <td className="px-4 py-4 text-sm font-medium text-slate-900" data-id="element-2770" data-mp="zy61szoyhbiy">
                        {item.description}
                      </td>}
                    {visibleColumns.includes('amount') && <td className="px-4 py-4 text-sm font-medium text-slate-900" data-id="element-2771" data-mp="12hj6sskurgrx">
                        {item.amount}
                      </td>}
                    {visibleColumns.includes('status') && <td className="px-4 py-4" data-id="element-2772" data-mp="1ca850lre6y5b">
                        <Badge variant={item.status === 'Pagata' ? 'success' : item.status === 'In attesa' ? 'warning' : item.status === 'Scaduta' ? 'danger' : 'neutral'} data-id="element-2773" data-mp="1h5x7oxypkv5u">
                          {item.status}
                        </Badge>
                      </td>}
                    {visibleColumns.includes('dueDate') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-2774" data-mp="28zz6x620jnl5">
                        {item.dueDate}
                      </td>}
                    {visibleColumns.includes('actions') && <td className="px-4 py-4 pr-6" data-id="element-2775" data-mp="3a1o4ltzv0zmd">
                        {actionButtons(item, 'fattura')}
                      </td>}
                  </tr>)}
              </tbody>
            </table>
          </div>
          <div className="border-t border-slate-100" data-id="element-2776" data-mp="2ia8jk4dybky9">
            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={goToPage} totalItems={totalItems} pageSize={pageSize} onPageSizeChange={setPageSize} data-id="element-2777" data-mp="2dbniy3usez9a" />
          </div>
        </Card>

        <Modal isOpen={isViewOpen} onClose={() => setIsViewOpen(false)} title="Dettaglio fattura" size="lg" data-id="element-2778" data-mp="6jmoyyo5ikln">
          {selectedItem && <div className="space-y-6" data-id="element-2779" data-mp="a1txk6n3dz6m">
              <div className="grid grid-cols-2 gap-4" data-id="element-2780" data-mp="a0sd5bk6s0c8">
                <div data-id="element-2781" data-mp="e4gjjqx30pvw">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2782" data-mp="e5047fe49sdm">
                    Numero
                  </span>
                  <p className="font-mono font-medium mt-1" data-id="element-2783" data-mp="6a6pyndxqoq">
                    {selectedItem.number}
                  </p>
                </div>
                <div data-id="element-2784" data-mp="ssl28uw4x1eo">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2785" data-mp="su5x6lmd2md2">
                    Stato
                  </span>
                  <div className="mt-1" data-id="element-2786" data-mp="xtqyv3sx18th">
                    <Badge variant={selectedItem.status === 'Pagata' ? 'success' : selectedItem.status === 'In attesa' ? 'warning' : 'danger'} data-id="element-2787" data-mp="j3tfr29uf9b5">
                      {selectedItem.status}
                    </Badge>
                  </div>
                </div>
                <div className="col-span-full" data-id="element-2788" data-mp="1qc1s1q4citym">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2789" data-mp="1lgcqjh8rn2zo">
                    Descrizione
                  </span>
                  <p className="font-medium mt-1" data-id="element-2790" data-mp="1gib4cng1mnjz">{selectedItem.description}</p>
                </div>
                <div data-id="element-2791" data-mp="1cgzecgt9c0b9">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2792" data-mp="17fa2iyy7nim4">
                    Importo
                  </span>
                  <p className="font-medium mt-1" data-id="element-2793" data-mp="12o1rxzl59r7d">{selectedItem.amount}</p>
                </div>
                <div data-id="element-2794" data-mp="xqs24itufdj0">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2795" data-mp="xq0a1hzgfxxh">
                    Scadenza
                  </span>
                  <p className="font-medium mt-1" data-id="element-2796" data-mp="ooso7ze4kpka">{selectedItem.dueDate}</p>
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-2797" data-mp="jpfssq96ua63">
                <Button variant="flat" color="default" onClick={() => setIsViewOpen(false)} data-id="element-2798" data-mp="2vdktiqh32rp4">
                  Chiudi
                </Button>
                <Button onClick={() => {
              setIsViewOpen(false);
              handleEdit(selectedItem);
            }} startContent={<Edit className="h-4 w-4" data-id="element-2800" data-mp="2cn37t5uo4wd9" />} data-id="element-2799" data-mp="34dr526t81hln">
                  Modifica
                </Button>
              </div>
            </div>}
        </Modal>

        <Modal isOpen={isEditOpen} onClose={() => setIsEditOpen(false)} title={selectedItem ? 'Modifica fattura' : 'Nuova fattura'} size="lg" data-id="element-2801" data-mp="2gm2647lw9uvq">
          <AnimatePresence mode="wait" data-id="element-2802" data-mp="2qgav4lbpv5p5">
            {saveSuccess ? <SaveSuccessView label="Fattura salvata con successo" data-id="element-2803" data-mp="1syawjbrqnvy5" /> : <motion.div key="form" initial={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} data-id="element-2804" data-mp="1xwm5zle4g4fu">
                <div className="space-y-6" data-id="element-2805" data-mp="3vnc6rmy50vsn">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2806" data-mp="d79rbmifjksi">
                    <Input label="Numero" value={formData.number || ''} onChange={e => setFormData({
                  ...formData,
                  number: e.target.value
                })} data-id="element-2807" data-mp="d9cpwuxnem8u" />
                    <Input label="Importo" value={formData.amount || ''} onChange={e => setFormData({
                  ...formData,
                  amount: e.target.value
                })} data-id="element-2808" data-mp="w5ch43isxlrd" />
                    <Input label="Descrizione" value={formData.description || ''} onChange={e => setFormData({
                  ...formData,
                  description: e.target.value
                })} required className="col-span-full" data-id="element-2809" data-mp="2js743oxn7h3t" />
                    <Select label="Stato" value={formData.status || ''} onChange={e => setFormData({
                  ...formData,
                  status: e.target.value
                })} options={[{
                  value: 'Bozza',
                  label: 'Bozza'
                }, {
                  value: 'In attesa',
                  label: 'In attesa'
                }, {
                  value: 'Pagata',
                  label: 'Pagata'
                }, {
                  value: 'Scaduta',
                  label: 'Scaduta'
                }]} required data-id="element-2810" data-mp="203ga82qo44p0" />
                    <Input label="Data scadenza" type="date" value={formData.dueDate || ''} onChange={e => setFormData({
                  ...formData,
                  dueDate: e.target.value
                })} data-id="element-2811" data-mp="bk2bzpuy8mn5" />
                  </div>
                  <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-2812" data-mp="ud69jmbafjn1">
                    <Button variant="flat" color="default" onClick={() => setIsEditOpen(false)} data-id="element-2813" data-mp="pj20mxigp1jb">
                      Annulla
                    </Button>
                    <Button onClick={handleSave} startContent={<Save className="h-4 w-4" data-id="element-2815" data-mp="1bssaa6rv9k3o" />} data-id="element-2814" data-mp="16t5wnw84m1uc">
                      Salva fattura
                    </Button>
                  </div>
                </div>
              </motion.div>}
          </AnimatePresence>
        </Modal>

        <DeleteModal isOpen={isDeleteOpen} onClose={() => setIsDeleteOpen(false)} onConfirm={confirmDelete} itemLabel={selectedItem?.number || ''} deleteSuccess={deleteSuccess} data-id="element-2816" data-mp="1krnghiuwuu6z" />
      </Layout>;
  }
  // ─── Training Section ─────────────────────────────────────────────────────
  if (sectionId === 'training') {
    return <Layout data-id="element-2817" data-mp="23khlbjytl5xh">
        {header}
        {toolbar}
        <Card noPadding className="overflow-hidden" data-id="element-2818" data-mp="2oumti43bimps">
          <div className="overflow-x-auto" data-id="element-2819" data-mp="2otbra8pitn0s">
            <table className="w-full text-left border-collapse table-auto" data-id="element-2820" data-mp="2ktl40ez7dp10">
              <thead data-id="element-2821" data-mp="2kvnsgukpja3m">
                <tr className="bg-slate-50/50 border-b border-slate-100 text-xs uppercase tracking-wider text-slate-500 font-semibold" data-id="element-2822" data-mp="2ysu7qf682qo3">
                  {cols.filter(c => visibleColumns.includes(c.key)).map(col => <th key={col.key} className="px-4 py-4" data-id="element-2823" data-mp="262c9pskcvfhj">
                        {col.label}
                      </th>)}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-2824" data-mp="2fvj9mxwbu3a8">
                {paginatedData.map(item => <tr key={item.id} className="hover:bg-slate-50/80 transition-colors group" data-id="element-2825" data-mp="2b1f1wcf8lr83">
                    {visibleColumns.includes('code') && <td className="px-4 py-4 font-mono text-xs text-slate-500" data-id="element-2826" data-mp="1iczykn5gwxcl">
                        {item.code}
                      </td>}
                    {visibleColumns.includes('title') && <td className="px-4 py-4 text-sm font-medium text-slate-900" data-id="element-2827" data-mp="3l219z1nls38o">
                        {item.title}
                      </td>}
                    {visibleColumns.includes('type') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-2828" data-mp="gokzlcbxkrox">
                        {item.type}
                      </td>}
                    {visibleColumns.includes('mode') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-2829" data-mp="7mbr0s0838e6">
                        {item.mode}
                      </td>}
                    {visibleColumns.includes('date') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-2830" data-mp="2sf6u6i1qds1j">
                        {item.date}
                      </td>}
                    {visibleColumns.includes('enrolled') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-2831" data-mp="20di2agu2dfg9">
                        {item.enrolled}
                      </td>}
                    {visibleColumns.includes('status') && <td className="px-4 py-4" data-id="element-2832" data-mp="29eq1d14emkbe">
                        <Badge variant={item.status === 'In corso' ? 'success' : item.status === 'Programmato' ? 'info' : 'neutral'} data-id="element-2833" data-mp="29cc4qnu2t01q">
                          {item.status}
                        </Badge>
                      </td>}
                    {visibleColumns.includes('actions') && <td className="px-4 py-4 pr-6" data-id="element-2834" data-mp="xyhuinhxwmtx">
                        {actionButtons(item, 'corso')}
                      </td>}
                  </tr>)}
              </tbody>
            </table>
          </div>
          <div className="border-t border-slate-100" data-id="element-2835" data-mp="leks631n9x80">
            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={goToPage} totalItems={totalItems} pageSize={pageSize} onPageSizeChange={setPageSize} data-id="element-2836" data-mp="he0yxak6zdr5" />
          </div>
        </Card>

        <Modal isOpen={isViewOpen} onClose={() => setIsViewOpen(false)} title="Dettaglio corso" size="lg" data-id="element-2837" data-mp="2p4r9em1s2r2">
          {selectedItem && <div className="space-y-6" data-id="element-2838" data-mp="3pvopg8o9awxi">
              <div className="grid grid-cols-2 gap-4" data-id="element-2839" data-mp="3pwqbf86t93u0">
                <div data-id="element-2840" data-mp="3kw2b1vwozan4">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2841" data-mp="3kuhj8e1ri5sy">
                    Codice
                  </span>
                  <p className="font-mono font-medium mt-1" data-id="element-2842" data-mp="3fz25rn7o2bx2">
                    {selectedItem.code}
                  </p>
                </div>
                <div data-id="element-2843" data-mp="3b8ghrhu5p9qa">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2844" data-mp="3b9rp1rmt8zm5">
                    Stato
                  </span>
                  <div className="mt-1" data-id="element-2845" data-mp="2wdaakrjbr4vd">
                    <Badge variant={selectedItem.status === 'In corso' ? 'success' : selectedItem.status === 'Programmato' ? 'info' : 'neutral'} data-id="element-2846" data-mp="31b241pzmaz1o">
                      {selectedItem.status}
                    </Badge>
                  </div>
                </div>
                <div className="col-span-full" data-id="element-2847" data-mp="bt1quejtnzij">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2848" data-mp="bs9uv2mbbzrq">
                    Titolo
                  </span>
                  <p className="font-medium text-lg mt-1" data-id="element-2849" data-mp="glmib9f9a0iw">
                    {selectedItem.title}
                  </p>
                </div>
                <div data-id="element-2850" data-mp="1wbvr5ln589x7">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2851" data-mp="1waaunqh9qgwp">
                    Tipologia
                  </span>
                  <p className="font-medium mt-1" data-id="element-2852" data-mp="2b1tczuxlonj8">{selectedItem.type}</p>
                </div>
                <div data-id="element-2853" data-mp="2639mribz4pl2">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2854" data-mp="1ib2tis1joypv">
                    Modalità
                  </span>
                  <p className="font-medium mt-1" data-id="element-2855" data-mp="1ddjg1uco8pyz">{selectedItem.mode}</p>
                </div>
                <div data-id="element-2856" data-mp="1rcsmi2b2s5pr">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2857" data-mp="1rdc75qs4187h">
                    Data
                  </span>
                  <p className="font-medium mt-1" data-id="element-2858" data-mp="32qwej2y3rjs6">{selectedItem.date}</p>
                </div>
                <div data-id="element-2859" data-mp="2ypkwpjlh02uj">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2860" data-mp="2yq480hgm7szl">
                    Iscritti
                  </span>
                  <p className="font-medium mt-1" data-id="element-2861" data-mp="3u1c4azuncrny">{selectedItem.enrolled}</p>
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-2862" data-mp="1y1fwpmx5lid">
                <Button variant="flat" color="default" onClick={() => setIsViewOpen(false)} data-id="element-2863" data-mp="6tqi70qbpgid">
                  Chiudi
                </Button>
                <Button onClick={() => {
              setIsViewOpen(false);
              handleEdit(selectedItem);
            }} startContent={<Edit className="h-4 w-4" data-id="element-2865" data-mp="pmb6e4v2uxfu" />} data-id="element-2864" data-mp="gr4uco57rj4v">
                  Modifica
                </Button>
              </div>
            </div>}
        </Modal>

        <Modal isOpen={isEditOpen} onClose={() => setIsEditOpen(false)} title={selectedItem ? 'Modifica corso' : 'Nuovo corso'} size="lg" data-id="element-2866" data-mp="zl0dr3pckzhu">
          <AnimatePresence mode="wait" data-id="element-2867" data-mp="2p1vxiwu0bb8i">
            {saveSuccess ? <SaveSuccessView label="Corso salvato con successo" data-id="element-2868" data-mp="2k215gpyhwflv" /> : <motion.div key="form" initial={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} data-id="element-2869" data-mp="2f2okybv3jkmg">
                <div className="space-y-6" data-id="element-2870" data-mp="37rvib7ite464">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2871" data-mp="37qsux9kpajuy">
                    <Input label="Codice" value={formData.code || ''} onChange={e => setFormData({
                  ...formData,
                  code: e.target.value
                })} disabled={!!selectedItem} data-id="element-2872" data-mp="32tagx4rtteg3" />
                    <Select label="Stato" value={formData.status || ''} onChange={e => setFormData({
                  ...formData,
                  status: e.target.value
                })} options={[{
                  value: 'Programmato',
                  label: 'Programmato'
                }, {
                  value: 'In corso',
                  label: 'In corso'
                }, {
                  value: 'Completato',
                  label: 'Completato'
                }]} required data-id="element-2873" data-mp="3lq28s2lfp1vr" />
                    <Input label="Titolo" value={formData.title || ''} onChange={e => setFormData({
                  ...formData,
                  title: e.target.value
                })} required className="col-span-full" data-id="element-2874" data-mp="2lif2xy1hmex9" />
                    <Select label="Tipologia" value={formData.type || ''} onChange={e => setFormData({
                  ...formData,
                  type: e.target.value
                })} options={[{
                  value: 'Sicurezza',
                  label: 'Sicurezza'
                }, {
                  value: 'HACCP',
                  label: 'HACCP'
                }, {
                  value: 'Privacy',
                  label: 'Privacy'
                }]} required data-id="element-2875" data-mp="om0vhomhkz9b" />
                    <Select label="Modalità" value={formData.mode || ''} onChange={e => setFormData({
                  ...formData,
                  mode: e.target.value
                })} options={[{
                  value: 'Sede SEF',
                  label: 'Sede SEF'
                }, {
                  value: 'Sede cliente',
                  label: 'Sede cliente'
                }, {
                  value: 'A distanza',
                  label: 'A distanza'
                }]} required data-id="element-2876" data-mp="2ngzttx2oh22l" />
                    <DatePicker label="Data" value={formData.date || ''} onChange={e => setFormData({
                  ...formData,
                  date: e.target.value
                })} data-id="element-2877" data-mp="1dax347aas231" />
                  </div>
                  <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-2878" data-mp="zhxn846ls5uy">
                    <Button variant="flat" color="default" onClick={() => setIsEditOpen(false)} data-id="element-2879" data-mp="uikoal2gj8c0">
                      Annulla
                    </Button>
                    <Button onClick={handleSave} startContent={<Save className="h-4 w-4" data-id="element-2881" data-mp="2fxqfdpfbed6o" />} data-id="element-2880" data-mp="glflcty1ipkz">
                      Salva corso
                    </Button>
                  </div>
                </div>
              </motion.div>}
          </AnimatePresence>
        </Modal>

        <DeleteModal isOpen={isDeleteOpen} onClose={() => setIsDeleteOpen(false)} onConfirm={confirmDelete} itemLabel={selectedItem?.title || ''} deleteSuccess={deleteSuccess} data-id="element-2882" data-mp="348oxsrjwtkg0" />
      </Layout>;
  }
  // ─── Operations Section ───────────────────────────────────────────────────
  if (sectionId === 'operations') {
    return <Layout data-id="element-2883" data-mp="2kn2f4moti41d">
        {header}
        {toolbar}
        <Card noPadding className="overflow-hidden" data-id="element-2884" data-mp="2gk5uyrjmpzvb">
          <div className="overflow-x-auto" data-id="element-2885" data-mp="2gim9mmj48ymo">
            <table className="w-full text-left border-collapse table-auto" data-id="element-2886" data-mp="2bkkw0h4glril">
              <thead data-id="element-2887" data-mp="2bmnqpegk48tn">
                <tr className="bg-slate-50/50 border-b border-slate-100 text-xs uppercase tracking-wider text-slate-500 font-semibold" data-id="element-2888" data-mp="26m9j1xivb0w5">
                  {cols.filter(c => visibleColumns.includes(c.key)).map(col => <th key={col.key} className="px-4 py-4" data-id="element-2889" data-mp="4sk036e9j2sb">
                        {col.label}
                      </th>)}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-2890" data-mp="hb5sf9airoeo">
                {paginatedData.map(item => <tr key={item.id} className="hover:bg-slate-50/80 transition-colors group" data-id="element-2891" data-mp="m5s65j42pt1x">
                    {visibleColumns.includes('code') && <td className="px-4 py-4 font-mono text-xs text-slate-500" data-id="element-2892" data-mp="3ujir6yehgj6v">
                        {item.code}
                      </td>}
                    {visibleColumns.includes('activityType') && <td className="px-4 py-4 text-sm font-medium text-slate-900" data-id="element-2893" data-mp="8cke5qc2yvjp">
                        {item.activityType}
                      </td>}
                    {visibleColumns.includes('ambito') && <td className="px-4 py-4" data-id="element-2894" data-mp="1jpmo42jocmuw">
                        <Badge variant="neutral" className="bg-slate-100 text-slate-600 border-slate-200" data-id="element-2895" data-mp="3fu2nlu79ix1u">
                          {item.ambito}
                        </Badge>
                      </td>}
                    {visibleColumns.includes('contract') && <td className="px-4 py-4 font-mono text-xs text-slate-500" data-id="element-2896" data-mp="3krujt0exx5bu">
                        {item.contract}
                      </td>}
                    {visibleColumns.includes('operator') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-2897" data-mp="35wo9w2cq4md8">
                        {item.operator}
                      </td>}
                    {visibleColumns.includes('status') && <td className="px-4 py-4" data-id="element-2898" data-mp="2despuda3fx9t">
                        <Badge variant={statusColors[item.status] as any || 'neutral'} data-id="element-2899" data-mp="29ei8y5krjmg6">
                          {statusLabels[item.status] || item.status}
                        </Badge>
                      </td>}
                    {visibleColumns.includes('deadline') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-2900" data-mp="1ub8q4dpmhwqg">
                        {item.deadline}
                      </td>}
                    {visibleColumns.includes('actions') && <td className="px-4 py-4 pr-6" data-id="element-2901" data-mp="2mz4mfhba5mmp">
                        {actionButtons(item, 'attività')}
                      </td>}
                  </tr>)}
              </tbody>
            </table>
          </div>
          <div className="border-t border-slate-100" data-id="element-2902" data-mp="2d3t6wws1sbdw">
            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={goToPage} totalItems={totalItems} pageSize={pageSize} onPageSizeChange={setPageSize} data-id="element-2903" data-mp="wmypa21kpf0x" />
          </div>
        </Card>

        <Modal isOpen={isViewOpen} onClose={() => setIsViewOpen(false)} title={selectedItem ? `Dettaglio attività — ${selectedItem.code}` : ''} size="lg" data-id="element-2904" data-mp="1d2hbokmv6q8f">
          {selectedItem && <div className="space-y-6" data-id="element-2905" data-mp="20sjyal80f3zn">
              <div className="grid grid-cols-2 gap-4" data-id="element-2906" data-mp="20riqcpmaixm5">
                <div data-id="element-2907" data-mp="1vu0dio55jy8v">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2908" data-mp="1vth27qa0c83t">
                    Codice
                  </span>
                  <p className="font-mono font-medium mt-1" data-id="element-2909" data-mp="1qst5qp30ez27">
                    {selectedItem.code}
                  </p>
                </div>
                <div data-id="element-2910" data-mp="fg0ergi1men7">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2911" data-mp="fgjwaw3s710p">
                    Stato
                  </span>
                  <div className="mt-1" data-id="element-2912" data-mp="aiifkhttvdse">
                    <Badge variant={statusColors[selectedItem.status] as any || 'neutral'} data-id="element-2913" data-mp="2m49gjvw3luxj">
                      {statusLabels[selectedItem.status] || selectedItem.status}
                    </Badge>
                  </div>
                </div>
                <div className="col-span-full" data-id="element-2914" data-mp="30u8crt43ewad">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2915" data-mp="35s0efeujhwrr">
                    Tipologia attività
                  </span>
                  <p className="font-medium mt-1" data-id="element-2916" data-mp="39rr0j594fopy">
                    {selectedItem.activityType}
                  </p>
                </div>
                <div data-id="element-2917" data-mp="3jrhv16xom3q1">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2918" data-mp="3js1ckmjf6q3j">
                    Ambito
                  </span>
                  <p className="font-medium mt-1" data-id="element-2919" data-mp="1kfyp717odbkl">{selectedItem.ambito}</p>
                </div>
                <div data-id="element-2920" data-mp="1pe0h8bn2wl87">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2921" data-mp="1pfl2tbrf0ttx">
                    Contratto
                  </span>
                  <p className="font-mono font-medium mt-1" data-id="element-2922" data-mp="1dbpcdujziark">
                    {selectedItem.contract}
                  </p>
                </div>
                <div data-id="element-2923" data-mp="1rca0954rawrx">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2924" data-mp="1r8m6q5zbaad7">
                    Operatore
                  </span>
                  <p className="font-medium mt-1" data-id="element-2925" data-mp="20avggtmr9zpj">{selectedItem.operator}</p>
                </div>
                <div data-id="element-2926" data-mp="1wauwktgw0wb3">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2927" data-mp="1w9jvj1etu2no">
                    Zona
                  </span>
                  <p className="font-medium mt-1" data-id="element-2928" data-mp="2a3aujfuxm455">{selectedItem.zone}</p>
                </div>
                <div data-id="element-2929" data-mp="254pubgfojkgp">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2930" data-mp="2jze5nb7rhd48">
                    Scadenza
                  </span>
                  <p className="font-medium mt-1" data-id="element-2931" data-mp="2f1ughg9izfxi">{selectedItem.deadline}</p>
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-2932" data-mp="2yn4g7hj14csc">
                <Button variant="flat" color="default" onClick={() => setIsViewOpen(false)} data-id="element-2933" data-mp="32nelst2wt16o">
                  Chiudi
                </Button>
                <Button onClick={() => {
              setIsViewOpen(false);
              handleEdit(selectedItem);
            }} startContent={<Edit className="h-4 w-4" data-id="element-2935" data-mp="2jpvje8ayigt8" />} data-id="element-2934" data-mp="2tlf6c57jm55u">
                  Modifica
                </Button>
              </div>
            </div>}
        </Modal>

        <Modal isOpen={isEditOpen} onClose={() => setIsEditOpen(false)} title={selectedItem ? 'Modifica attività' : 'Nuova attività'} size="lg" data-id="element-2936" data-mp="2avyz6z1cregg">
          <AnimatePresence mode="wait" data-id="element-2937" data-mp="20yuez4r2ht2a">
            {saveSuccess ? <SaveSuccessView label="Attività salvata con successo" data-id="element-2938" data-mp="2gglvaz156vmo" /> : <motion.div key="form" initial={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} data-id="element-2939" data-mp="2ge9if0a8rbgh">
                <div className="space-y-6" data-id="element-2940" data-mp="22h3016tfjers">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2941" data-mp="22gb5lzbkjn33">
                    <Input label="Codice" value={formData.code || ''} onChange={e => setFormData({
                  ...formData,
                  code: e.target.value
                })} data-id="element-2942" data-mp="1xhzznzoe9wq5" />
                    <Select label="Tipologia attività" value={formData.activityType || ''} onChange={e => setFormData({
                  ...formData,
                  activityType: e.target.value
                })} options={['Sopralluogo periodico', 'Sopralluogo iniziale', 'Backoffice', 'Riunione in ufficio', 'Consulenza via email'].map(t => ({
                  value: t,
                  label: t
                }))} required data-id="element-2943" data-mp="1joic83sl7zuu" />
                    <Select label="Ambito" value={formData.ambito || ''} onChange={e => setFormData({
                  ...formData,
                  ambito: e.target.value
                })} options={['Sicurezza', 'Privacy', 'HACCP', 'Ambiente', 'Formazione'].map(a => ({
                  value: a,
                  label: a
                }))} required data-id="element-2944" data-mp="luhhb2m2s5n1" />
                    <Select label="Operatore" value={formData.operator || ''} onChange={e => setFormData({
                  ...formData,
                  operator: e.target.value
                })} options={[{
                  value: 'Alessio',
                  label: 'Alessio'
                }, {
                  value: 'Lorenzo',
                  label: 'Lorenzo'
                }, {
                  value: 'Gian Franco',
                  label: 'Gian Franco'
                }, {
                  value: 'Tiziano',
                  label: 'Tiziano'
                }]} required data-id="element-2945" data-mp="32lf0g2b7duiy" />
                    <Select label="Stato" value={formData.status || ''} onChange={e => setFormData({
                  ...formData,
                  status: e.target.value
                })} options={Object.entries(statusLabels).map(([v, l]) => ({
                  value: v,
                  label: l
                }))} required data-id="element-2946" data-mp="1va2dby8qcfvu" />
                    <DatePicker label="Scadenza" value={formData.deadline || ''} onChange={e => setFormData({
                  ...formData,
                  deadline: e.target.value
                })} data-id="element-2947" data-mp="2nzrau1m8dxsp" />
                  </div>
                  <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-2948" data-mp="1zdhwac202boh">
                    <Button variant="flat" color="default" onClick={() => setIsEditOpen(false)} data-id="element-2949" data-mp="24ckzrs2c09uc">
                      Annulla
                    </Button>
                    <Button onClick={handleSave} startContent={<Save className="h-4 w-4" data-id="element-2951" data-mp="2icm0n7iljh9t" />} data-id="element-2950" data-mp="2dec5uy0saioq">
                      Salva attività
                    </Button>
                  </div>
                </div>
              </motion.div>}
          </AnimatePresence>
        </Modal>

        <DeleteModal isOpen={isDeleteOpen} onClose={() => setIsDeleteOpen(false)} onConfirm={confirmDelete} itemLabel={selectedItem?.code || ''} deleteSuccess={deleteSuccess} data-id="element-2952" data-mp="o05v68xmk760" />
      </Layout>;
  }
  // ─── Non Conformities Section ─────────────────────────────────────────────
  if (sectionId === 'non_conformities') {
    return <Layout data-id="element-2953" data-mp="1o2vdz8b67xi">
        {header}
        {toolbar}
        <Card noPadding className="overflow-hidden" data-id="element-2954" data-mp="zcmq49kixgy0">
          <div className="overflow-x-auto" data-id="element-2955" data-mp="zbbks5bfk14s">
            <table className="w-full text-left border-collapse table-auto" data-id="element-2956" data-mp="ufmme5b5cqa2">
              <thead data-id="element-2957" data-mp="peyuzij0a7fb">
                <tr className="bg-slate-50/50 border-b border-slate-100 text-xs uppercase tracking-wider text-slate-500 font-semibold" data-id="element-2958" data-mp="pegsw59nwyzh">
                  {cols.filter(c => visibleColumns.includes(c.key)).map(col => <th key={col.key} className="px-4 py-4" data-id="element-2959" data-mp="364dq7g2zv4ao">
                        {col.label}
                      </th>)}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-2960" data-mp="12m5tochrbqw0">
                {paginatedData.map(item => <tr key={item.id} className="hover:bg-slate-50/80 transition-colors group" data-id="element-2961" data-mp="nxjimmz5xlal">
                    {visibleColumns.includes('number') && <td className="px-4 py-4 font-mono text-xs text-slate-500" data-id="element-2962" data-mp="1glnob8hqhyda">
                        {item.number}
                      </td>}
                    {visibleColumns.includes('type') && <td className="px-4 py-4" data-id="element-2963" data-mp="17kpicknqjgs2">
                        <Badge variant={item.type === 'Non conformità maggiore' ? 'danger' : item.type === 'Non conformità minore' ? 'warning' : 'info'} data-id="element-2964" data-mp="1bmkihltxjo49">
                          {item.type}
                        </Badge>
                      </td>}
                    {visibleColumns.includes('origin') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-2965" data-mp="2lki6aw9trhk">
                        {item.origin}
                      </td>}
                    {visibleColumns.includes('description') && <td className="px-4 py-4 text-sm text-slate-600 max-w-xs truncate" data-id="element-2966" data-mp="313zlr03w0wb2">
                        {item.description}
                      </td>}
                    {visibleColumns.includes('date') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-2967" data-mp="3b148b11natse">
                        {item.date}
                      </td>}
                    {visibleColumns.includes('responsible') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-2968" data-mp="2icyl87n4cspe">
                        {item.responsible}
                      </td>}
                    {visibleColumns.includes('status') && <td className="px-4 py-4" data-id="element-2969" data-mp="3c0ur57qvgn76">
                        <Badge variant={item.status === 'Aperta' ? 'danger' : item.status === 'Sospesa' ? 'warning' : 'success'} data-id="element-2970" data-mp="3guyznvfsd3ad">
                          {item.status}
                        </Badge>
                      </td>}
                    {visibleColumns.includes('actions') && <td className="px-4 py-4 pr-6" data-id="element-2971" data-mp="330g52663342m">
                        <div className="flex items-center gap-1" data-id="element-2972" data-mp="dc5p7rib0ukf">
                          <Tooltip content="Visualizza NC" data-id="element-2973" data-mp="ic1rnmbcsdxv">
                            <Button variant="light" size="sm" className="!px-1" onClick={() => handleView(item)} data-id="element-2974" data-mp="iagz228ln52n">
                              <Eye className="h-4 w-4 text-slate-500" data-id="element-2975" data-mp="1t3hne77v8ycj" />
                            </Button>
                          </Tooltip>
                          <Tooltip content="Invia report" data-id="element-2976" data-mp="1o3dfv6oxrdyg">
                            <Button variant="light" size="sm" className="!px-1" data-id="element-2977" data-mp="1k4w6shlyftee">
                              <Mail className="h-4 w-4 text-blue-500" data-id="element-2978" data-mp="1f48gjy5jvgl8" />
                            </Button>
                          </Tooltip>
                          <Tooltip content="Modifica NC" data-id="element-2979" data-mp="1a5xalyidlq8a">
                            <Button variant="light" size="sm" className="!px-1" onClick={() => handleEdit(item)} data-id="element-2980" data-mp="157dn3to4w6dt">
                              <Edit className="h-4 w-4 text-seform-orange" data-id="element-2981" data-mp="2zisf6h4mahwq" />
                            </Button>
                          </Tooltip>
                          <Tooltip content="Elimina NC" data-id="element-2982" data-mp="2ddaqfp9g8q7j">
                            <Button variant="light" size="sm" className="!px-1" onClick={() => handleDelete(item)} data-id="element-2983" data-mp="1zmxgxigwt3g3">
                              <Trash2 className="h-4 w-4 text-red-500" data-id="element-2984" data-mp="1pngd76l7wzmr" />
                            </Button>
                          </Tooltip>
                        </div>
                      </td>}
                  </tr>)}
              </tbody>
            </table>
          </div>
          <div className="border-t border-slate-100" data-id="element-2985" data-mp="1krre14d41kqz">
            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={goToPage} totalItems={totalItems} pageSize={pageSize} onPageSizeChange={setPageSize} data-id="element-2986" data-mp="3b35q42qujd0q" />
          </div>
        </Card>

        <Modal isOpen={isViewOpen} onClose={() => setIsViewOpen(false)} title={selectedItem ? `Dettaglio NC — ${selectedItem.number}` : ''} size="lg" data-id="element-2987" data-mp="zfsdksa7xqxy">
          {selectedItem && <div className="space-y-6" data-id="element-2988" data-mp="18e4afc1ohkyu">
              <div className="grid grid-cols-2 gap-4" data-id="element-2989" data-mp="1n3ttim9q89ne">
                <div data-id="element-2990" data-mp="1n2ih5y27ttko">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2991" data-mp="1i58tamuh5pyy">
                    Codice
                  </span>
                  <p className="font-mono font-medium mt-1" data-id="element-2992" data-mp="1w59q9r7mcd95">
                    {selectedItem.number}
                  </p>
                </div>
                <div data-id="element-2993" data-mp="1r3u4ty1j44q8">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2994" data-mp="1qqq3vajulua">
                    Stato
                  </span>
                  <div className="mt-1" data-id="element-2995" data-mp="3syczm4jy9ij2">
                    <Badge variant={selectedItem.status === 'Aperta' ? 'danger' : selectedItem.status === 'Sospesa' ? 'warning' : 'success'} data-id="element-2996" data-mp="3sze9i5p8byz7">
                      {selectedItem.status}
                    </Badge>
                  </div>
                </div>
                <div data-id="element-2997" data-mp="2s1oduwzzp7ya">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2998" data-mp="2s0ve4fzg7ipn">
                    Tipologia
                  </span>
                  <div className="mt-1" data-id="element-2999" data-mp="30urybp91yl2f">
                    <Badge variant={selectedItem.type === 'Non conformità maggiore' ? 'danger' : 'warning'} data-id="element-3000" data-mp="30tqb6meri84c">
                      {selectedItem.type}
                    </Badge>
                  </div>
                </div>
                <div data-id="element-3001" data-mp="3pfd527t4f91n">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-3002" data-mp="3pgy1k2yzx225">
                    Origine
                  </span>
                  <p className="font-medium mt-1" data-id="element-3003" data-mp="3ucwt1zfmf06s">{selectedItem.origin}</p>
                </div>
                <div className="col-span-full" data-id="element-3004" data-mp="3fjtappu7gif0">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-3005" data-mp="3kg0izpa2q604">
                    Descrizione
                  </span>
                  <p className="text-sm text-slate-700 bg-slate-50 rounded-lg p-3 border border-slate-100 mt-1" data-id="element-3006" data-mp="36o3tlszsia7f">
                    {selectedItem.description}
                  </p>
                </div>
                <div data-id="element-3007" data-mp="2wt24uy9ffa5d">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-3008" data-mp="31qbr651ir5oz">
                    Data rilevazione
                  </span>
                  <p className="font-medium mt-1" data-id="element-3009" data-mp="2nrlv8sqfrc27">{selectedItem.date}</p>
                </div>
                <div data-id="element-3010" data-mp="2rr2zxjy1g56b">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-3011" data-mp="2rrmm5cuq1fq5">
                    Responsabile
                  </span>
                  <p className="font-medium mt-1" data-id="element-3012" data-mp="tiozlesitbm1">{selectedItem.responsible}</p>
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-3013" data-mp="oiubfj04r04o">
                <Button variant="flat" color="default" onClick={() => setIsViewOpen(false)} data-id="element-3014" data-mp="jksq0rf8eoq1">
                  Chiudi
                </Button>
                <Button onClick={() => {
              setIsViewOpen(false);
              handleEdit(selectedItem);
            }} startContent={<Edit className="h-4 w-4" data-id="element-3016" data-mp="138s348x9q7ym" />} data-id="element-3015" data-mp="1c8wzlo0xbly0">
                  Modifica
                </Button>
              </div>
            </div>}
        </Modal>

        <Modal isOpen={isEditOpen} onClose={() => setIsEditOpen(false)} title={selectedItem ? 'Modifica non conformità' : 'Nuova non conformità'} size="lg" data-id="element-3017" data-mp="3rxzsqow2wogf">
          <AnimatePresence mode="wait" data-id="element-3018" data-mp="27hs8y6xmm3w4">
            {saveSuccess ? <SaveSuccessView label="Non conformità salvata con successo" data-id="element-3019" data-mp="2chd7whcsqc8u" /> : <motion.div key="form" initial={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} data-id="element-3020" data-mp="2cfjyiuastdx5">
                <div className="space-y-6" data-id="element-3021" data-mp="2r610cerci5uk">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3022" data-mp="2r4pvebm5ys1v">
                    <Input label="Codice" value={formData.number || ''} onChange={e => setFormData({
                  ...formData,
                  number: e.target.value
                })} disabled={!!selectedItem} data-id="element-3023" data-mp="2v7mh4b7034a1" />
                    <Select label="Tipologia" value={formData.type || ''} onChange={e => setFormData({
                  ...formData,
                  type: e.target.value
                })} options={[{
                  value: 'Non conformità maggiore',
                  label: 'Non conformità maggiore'
                }, {
                  value: 'Non conformità minore',
                  label: 'Non conformità minore'
                }, {
                  value: 'Osservazione',
                  label: 'Osservazione'
                }]} required data-id="element-3024" data-mp="ahqcc2ni17sx" />
                    <Select label="Origine" value={formData.origin || ''} onChange={e => setFormData({
                  ...formData,
                  origin: e.target.value
                })} options={[{
                  value: 'Interna',
                  label: 'Interna'
                }, {
                  value: 'Cliente (reclamo)',
                  label: 'Cliente (reclamo)'
                }, {
                  value: 'Fornitore',
                  label: 'Fornitore'
                }]} required data-id="element-3025" data-mp="305zcqeyvw3dg" />
                    <DatePicker label="Data rilevazione" value={formData.date || ''} onChange={e => setFormData({
                  ...formData,
                  date: e.target.value
                })} data-id="element-3026" data-mp="dze27royttyt" />
                    <Input label="Responsabile" value={formData.responsible || ''} onChange={e => setFormData({
                  ...formData,
                  responsible: e.target.value
                })} data-id="element-3027" data-mp="2ay36bvanfbu8" />
                    <Select label="Stato" value={formData.status || ''} onChange={e => setFormData({
                  ...formData,
                  status: e.target.value
                })} options={[{
                  value: 'Aperta',
                  label: 'Aperta'
                }, {
                  value: 'Sospesa',
                  label: 'Sospesa'
                }, {
                  value: 'Chiusa',
                  label: 'Chiusa'
                }]} required data-id="element-3028" data-mp="1s00njy8p2b4v" />
                    <div className="col-span-full" data-id="element-3029" data-mp="36z67tw5uqkkm">
                      <Textarea label="Descrizione" value={formData.description || ''} onChange={e => setFormData({
                    ...formData,
                    description: e.target.value
                  })} required data-id="element-3030" data-mp="3ay6op94rrd4z" />
                    </div>
                  </div>
                  <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-3031" data-mp="1laxwdroylm5h">
                    <Button variant="flat" color="default" onClick={() => setIsEditOpen(false)} data-id="element-3032" data-mp="1l8lhxoiettx6">
                      Annulla
                    </Button>
                    <Button onClick={handleSave} startContent={<Save className="h-4 w-4" data-id="element-3034" data-mp="24yf9q2l4k91e" />} data-id="element-3033" data-mp="1q8zo110gatsk">
                      Salva
                    </Button>
                  </div>
                </div>
              </motion.div>}
          </AnimatePresence>
        </Modal>

        <DeleteModal isOpen={isDeleteOpen} onClose={() => setIsDeleteOpen(false)} onConfirm={confirmDelete} itemLabel={selectedItem?.number || ''} deleteSuccess={deleteSuccess} data-id="element-3035" data-mp="2dxiv5d7rl0j2" />
      </Layout>;
  }
  // ─── Improvements Section ─────────────────────────────────────────────────
  if (sectionId === 'improvements') {
    return <Layout data-id="element-3036" data-mp="2qamkbysr8u1x">
        {header}
        {toolbar}
        <Card noPadding className="overflow-hidden" data-id="element-3037" data-mp="2v97cdaxus7fa">
          <div className="overflow-x-auto" data-id="element-3038" data-mp="2vb1wwo57pxiu">
            <table className="w-full text-left border-collapse table-auto" data-id="element-3039" data-mp="2hicbolxicwms">
              <thead data-id="element-3040" data-mp="2lj4il8iwqpfw">
                <tr className="bg-slate-50/50 border-b border-slate-100 text-xs uppercase tracking-wider text-slate-500 font-semibold" data-id="element-3041" data-mp="2licqi7oir9ud">
                  {cols.filter(c => visibleColumns.includes(c.key)).map(col => <th key={col.key} className="px-4 py-4" data-id="element-3042" data-mp="1yiomo1zvmojq">
                        {col.label}
                      </th>)}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-3043" data-mp="22m3f5s504xe2">
                {paginatedData.map(item => <tr key={item.id} className="hover:bg-slate-50/80 transition-colors group" data-id="element-3044" data-mp="sx2tlklk18y">
                    {visibleColumns.includes('number') && <td className="px-4 py-4 font-mono text-xs text-slate-500" data-id="element-3045" data-mp="18gvznkjui72a">
                        {item.number}
                      </td>}
                    {visibleColumns.includes('subject') && <td className="px-4 py-4 text-sm font-medium text-slate-900" data-id="element-3046" data-mp="1ha93vind0v3o">
                        {item.subject}
                      </td>}
                    {visibleColumns.includes('description') && <td className="px-4 py-4 text-sm text-slate-600 max-w-xs truncate" data-id="element-3047" data-mp="1r9q4hlnr8iss">
                        {item.description}
                      </td>}
                    {visibleColumns.includes('date') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-3048" data-mp="1th0o3o5by14">
                        {item.date}
                      </td>}
                    {visibleColumns.includes('responsible') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-3049" data-mp="3ugxk9os4ddfv">
                        {item.responsible}
                      </td>}
                    {visibleColumns.includes('status') && <td className="px-4 py-4" data-id="element-3050" data-mp="3ko9pezkp9jjs">
                        <Badge variant={item.status === 'Approvato' ? 'success' : item.status === 'Sospesa' ? 'warning' : 'info'} data-id="element-3051" data-mp="3gpk48ktf6xpl">
                          {item.status}
                        </Badge>
                      </td>}
                    {visibleColumns.includes('actions') && <td className="px-4 py-4 pr-6" data-id="element-3052" data-mp="2t0hpplu31kzb">
                        {actionButtons(item, 'miglioramento')}
                      </td>}
                  </tr>)}
              </tbody>
            </table>
          </div>
          <div className="border-t border-slate-100" data-id="element-3053" data-mp="2ugln2dibfwra">
            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={goToPage} totalItems={totalItems} pageSize={pageSize} onPageSizeChange={setPageSize} data-id="element-3054" data-mp="2udzhukiwdtc8" />
          </div>
        </Card>

        <Modal isOpen={isViewOpen} onClose={() => setIsViewOpen(false)} title={selectedItem ? `Dettaglio miglioramento — ${selectedItem.number}` : ''} size="lg" data-id="element-3055" data-mp="26oy54l317w15">
          {selectedItem && <div className="space-y-6" data-id="element-3056" data-mp="21pbrwbn7efsc">
              <div className="grid grid-cols-2 gap-4" data-id="element-3057" data-mp="1nrn8z9cp5ipx">
                <div data-id="element-3058" data-mp="1nu9dt187dk4g">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-3059" data-mp="1sqqcuqgxxiwb">
                    Codice
                  </span>
                  <p className="font-mono font-medium mt-1" data-id="element-3060" data-mp="195zkxd3dhu1s">
                    {selectedItem.number}
                  </p>
                </div>
                <div data-id="element-3061" data-mp="148g548r1197q">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-3062" data-mp="1i6e9clbmvp9i">
                    Stato
                  </span>
                  <div className="mt-1" data-id="element-3063" data-mp="1e3htv3he499s">
                    <Badge variant={selectedItem.status === 'Approvato' ? 'success' : selectedItem.status === 'Sospesa' ? 'warning' : 'info'} data-id="element-3064" data-mp="1s42hqe25wva5">
                      {selectedItem.status}
                    </Badge>
                  </div>
                </div>
                <div className="col-span-full" data-id="element-3065" data-mp="2av3d6s42q8oy">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-3066" data-mp="25vqriaoxv7ny">
                    Oggetto
                  </span>
                  <p className="font-medium mt-1" data-id="element-3067" data-mp="458cbfsulfxv">{selectedItem.subject}</p>
                </div>
                <div className="col-span-full" data-id="element-3068" data-mp="94cp0gezdvsy">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-3069" data-mp="931kufhmim1b">
                    Descrizione
                  </span>
                  <p className="text-sm text-slate-700 bg-slate-50 rounded-lg p-3 border border-slate-100 mt-1" data-id="element-3070" data-mp="hxy5c46snl4h">
                    {selectedItem.description}
                  </p>
                </div>
                <div data-id="element-3071" data-mp="3mcxv7hwat3oy">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-3072" data-mp="3mcfzcmdjsrhk">
                    Data proposta
                  </span>
                  <p className="font-medium mt-1" data-id="element-3073" data-mp="3vcckie0qb87v">{selectedItem.date}</p>
                </div>
                <div data-id="element-3074" data-mp="15najzjkw4le6">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-3075" data-mp="15o2520gv30q7">
                    Responsabile
                  </span>
                  <p className="font-medium mt-1" data-id="element-3076" data-mp="1akitkvrz3h3t">{selectedItem.responsible}</p>
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-3077" data-mp="1ks4vevisbjz4">
                <Button variant="flat" color="default" onClick={() => setIsViewOpen(false)} data-id="element-3078" data-mp="1ftafq2g46bj8">
                  Chiudi
                </Button>
                <Button onClick={() => {
              setIsViewOpen(false);
              handleEdit(selectedItem);
            }} startContent={<Edit className="h-4 w-4" data-id="element-3080" data-mp="wzepp41h5wxa" />} data-id="element-3079" data-mp="16x2s8g5bo8q0">
                  Modifica
                </Button>
              </div>
            </div>}
        </Modal>

        <Modal isOpen={isEditOpen} onClose={() => setIsEditOpen(false)} title={selectedItem ? 'Modifica miglioramento' : 'Nuovo miglioramento'} size="lg" data-id="element-3081" data-mp="o1cnwm9x308y">
          <AnimatePresence mode="wait" data-id="element-3082" data-mp="2ibyb6eb04wcd">
            {saveSuccess ? <SaveSuccessView label="Miglioramento salvato con successo" data-id="element-3083" data-mp="fpua1hltbcy6" /> : <motion.div key="form" initial={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} data-id="element-3084" data-mp="kivr9w9hrokx">
                <div className="space-y-6" data-id="element-3085" data-mp="askava8mfmx5">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3086" data-mp="yhdc9m80imz5">
                    <Input label="Codice" value={formData.number || ''} onChange={e => setFormData({
                  ...formData,
                  number: e.target.value
                })} disabled={!!selectedItem} data-id="element-3087" data-mp="yg247a7jat28" />
                    <DatePicker label="Data proposta" value={formData.date || ''} onChange={e => setFormData({
                  ...formData,
                  date: e.target.value
                })} data-id="element-3088" data-mp="3f6ir8nwrcr20" />
                    <div className="col-span-full" data-id="element-3089" data-mp="3gn62ml7it72q">
                      <Input label="Oggetto" value={formData.subject || ''} onChange={e => setFormData({
                    ...formData,
                    subject: e.target.value
                  })} required data-id="element-3090" data-mp="3bo36xrdfkd7f" />
                    </div>
                    <div className="col-span-full" data-id="element-3091" data-mp="2sxbs4aaobuln">
                      <Textarea label="Descrizione" value={formData.description || ''} onChange={e => setFormData({
                    ...formData,
                    description: e.target.value
                  })} required data-id="element-3092" data-mp="25899az4azvpk" />
                    </div>
                    <Input label="Responsabile" value={formData.responsible || ''} onChange={e => setFormData({
                  ...formData,
                  responsible: e.target.value
                })} data-id="element-3093" data-mp="1ompt8jog06f0" />
                    <Select label="Stato" value={formData.status || ''} onChange={e => setFormData({
                  ...formData,
                  status: e.target.value
                })} options={[{
                  value: 'In valutazione',
                  label: 'In valutazione'
                }, {
                  value: 'Approvato',
                  label: 'Approvato'
                }, {
                  value: 'Sospesa',
                  label: 'Sospesa'
                }, {
                  value: 'Scartata',
                  label: 'Scartata'
                }]} required data-id="element-3094" data-mp="27eiluczlnpm7" />
                  </div>
                  <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-3095" data-mp="jnb5eszeom1z">
                    <Button variant="flat" color="default" onClick={() => setIsEditOpen(false)} data-id="element-3096" data-mp="eo6u9wsx8e90">
                      Annulla
                    </Button>
                    <Button onClick={handleSave} startContent={<Save className="h-4 w-4" data-id="element-3098" data-mp="1k3dnd7i45w18" />} data-id="element-3097" data-mp="1p2qazugt774v">
                      Salva
                    </Button>
                  </div>
                </div>
              </motion.div>}
          </AnimatePresence>
        </Modal>

        <DeleteModal isOpen={isDeleteOpen} onClose={() => setIsDeleteOpen(false)} onConfirm={confirmDelete} itemLabel={selectedItem?.number || ''} deleteSuccess={deleteSuccess} data-id="element-3099" data-mp="1b40didq2v5fz" />
      </Layout>;
  }
  // ─── Equipment Section ────────────────────────────────────────────────────
  if (sectionId === 'equipment') {
    return <Layout data-id="element-3100" data-mp="1u0id1hb649ms">
        {header}
        {toolbar}
        <Card noPadding className="overflow-hidden" data-id="element-3101" data-mp="2qj6927e21hq6">
          <div className="overflow-x-auto" data-id="element-3102" data-mp="2qkqzplx90gir">
            <table className="w-full text-left border-collapse table-auto" data-id="element-3103" data-mp="2lsptagmrhk0n">
              <thead data-id="element-3104" data-mp="2lqd4pkmi0bem">
                <tr className="bg-slate-50/50 border-b border-slate-100 text-xs uppercase tracking-wider text-slate-500 font-semibold" data-id="element-3105" data-mp="8a37yugd4ljn">
                  {cols.filter(c => visibleColumns.includes(c.key)).map(col => <th key={col.key} className="px-4 py-4" data-id="element-3106" data-mp="hcmafdg8kw5d">
                        {col.label}
                      </th>)}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-3107" data-mp="r8619lfy14n9">
                {paginatedData.map(item => <tr key={item.id} className="hover:bg-slate-50/80 transition-colors group" data-id="element-3108" data-mp="w5o36rvjmjlr">
                    {visibleColumns.includes('code') && <td className="px-4 py-4 font-mono text-xs text-slate-500" data-id="element-3109" data-mp="151x1g37sbc6b">
                        {item.code}
                      </td>}
                    {visibleColumns.includes('name') && <td className="px-4 py-4 text-sm font-medium text-slate-900" data-id="element-3110" data-mp="3b0vdlpiros97">
                        {item.name}
                      </td>}
                    {visibleColumns.includes('type') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-3111" data-mp="3krztcrg9oh53">
                        {item.type}
                      </td>}
                    {visibleColumns.includes('lastMaintenance') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-3112" data-mp="3tq35wy99y3kr">
                        {item.lastMaintenance}
                      </td>}
                    {visibleColumns.includes('nextMaintenance') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-3113" data-mp="2luyrx8jfg3f">
                        {item.nextMaintenance}
                      </td>}
                    {visibleColumns.includes('responsible') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-3114" data-mp="24e9m5btupcr8">
                        {item.responsible}
                      </td>}
                    {visibleColumns.includes('maintenanceType') && <td className="px-4 py-4" data-id="element-3115" data-mp="ow3e0tqhvp8d">
                        <Badge variant={item.maintenanceType === 'Ordinaria' ? 'info' : 'warning'} data-id="element-3116" data-mp="a5vyomvsambw">
                          {item.maintenanceType}
                        </Badge>
                      </td>}
                    {visibleColumns.includes('actions') && <td className="px-4 py-4 pr-6" data-id="element-3117" data-mp="3ne0fi5orplm1">
                        <div className="flex items-center gap-1" data-id="element-3118" data-mp="3sbijdhnxhan6">
                          <Tooltip content="Visualizza asset" data-id="element-3119" data-mp="1ljrhq6pt7f8e">
                            <Button variant="light" size="sm" className="!px-1" onClick={() => handleView(item)} data-id="element-3120" data-mp="1li6k28877g6b">
                              <Eye className="h-4 w-4 text-slate-500" data-id="element-3121" data-mp="3im8gej4yglcd" />
                            </Button>
                          </Tooltip>
                          <Tooltip content="Registra manutenzione" data-id="element-3122" data-mp="3dnnf0gdyvvmc">
                            <Button variant="light" size="sm" className="!px-1" data-id="element-3123" data-mp="38m7q2d8o54yo">
                              <Wrench className="h-4 w-4 text-blue-500" data-id="element-3124" data-mp="33oobtdbz0s6q" />
                            </Button>
                          </Tooltip>
                          <Tooltip content="Modifica asset" data-id="element-3125" data-mp="2yv3bvfqtho4w">
                            <Button variant="light" size="sm" className="!px-1" onClick={() => handleEdit(item)} data-id="element-3126" data-mp="2uw49a1sk63gm">
                              <Edit className="h-4 w-4 text-seform-orange" data-id="element-3127" data-mp="2fxk2uc4d05ih" />
                            </Button>
                          </Tooltip>
                          <Tooltip content="Elimina asset" data-id="element-3128" data-mp="25tvkiz41ew7l">
                            <Button variant="light" size="sm" className="!px-1" onClick={() => handleDelete(item)} data-id="element-3129" data-mp="2asf5oxat43yw">
                              <Trash2 className="h-4 w-4 text-red-500" data-id="element-3130" data-mp="1n3nlot7derx7" />
                            </Button>
                          </Tooltip>
                        </div>
                      </td>}
                  </tr>)}
              </tbody>
            </table>
          </div>
          <div className="border-t border-slate-100" data-id="element-3131" data-mp="21sa1sx4tnnph">
            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={goToPage} totalItems={totalItems} pageSize={pageSize} onPageSizeChange={setPageSize} data-id="element-3132" data-mp="21tutmezr4sjn" />
          </div>
        </Card>

        <Modal isOpen={isViewOpen} onClose={() => setIsViewOpen(false)} title={selectedItem ? `Dettaglio asset — ${selectedItem.code}` : ''} size="lg" data-id="element-3133" data-mp="119qcmzqcvinb">
          {selectedItem && <div className="space-y-6" data-id="element-3134" data-mp="161zv5up55gfk">
              <div className="grid grid-cols-2 gap-4" data-id="element-3135" data-mp="1631ioyncfve6">
                <div data-id="element-3136" data-mp="ibvtvtzfdsrq">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-3137" data-mp="idgotkpnjdq4">
                    Codice
                  </span>
                  <p className="font-mono font-medium mt-1" data-id="element-3138" data-mp="dfynoghvm2so">
                    {selectedItem.code}
                  </p>
                </div>
                <div data-id="element-3139" data-mp="nbifortbkhc5">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-3140" data-mp="na73q4pq039y">
                    Tipologia
                  </span>
                  <p className="font-medium mt-1" data-id="element-3141" data-mp="1yqxfe3cwxnay">{selectedItem.type}</p>
                </div>
                <div className="col-span-full" data-id="element-3142" data-mp="1yt9r3ys2v1fk">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-3143" data-mp="269epobsm564z">
                    Nome
                  </span>
                  <p className="font-medium mt-1" data-id="element-3144" data-mp="2b7g94xtyb7gz">{selectedItem.name}</p>
                </div>
                <div data-id="element-3145" data-mp="1xbkw38m8gqli">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-3146" data-mp="21bd8xxintiwo">
                    Responsabile
                  </span>
                  <p className="font-medium mt-1" data-id="element-3147" data-mp="2p1z6uvyy9msy">{selectedItem.responsible}</p>
                </div>
                <div data-id="element-3148" data-mp="2tyzcj5rw0njj">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-3149" data-mp="2tz93avkrayqa">
                    Tipo manutenzione
                  </span>
                  <div className="mt-1" data-id="element-3150" data-mp="2k9z836um8zxy">
                    <Badge variant={selectedItem.maintenanceType === 'Ordinaria' ? 'info' : 'warning'} data-id="element-3151" data-mp="2k8o7ffwgw8b2">
                      {selectedItem.maintenanceType}
                    </Badge>
                  </div>
                </div>
                <div data-id="element-3152" data-mp="2j8w06hcvbqmh">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-3153" data-mp="2j8dzn8j6aq8r">
                    Ultima manutenzione
                  </span>
                  <p className="font-medium mt-1" data-id="element-3154" data-mp="29azpfqnuexow">
                    {selectedItem.lastMaintenance}
                  </p>
                </div>
                <div data-id="element-3155" data-mp="36xfbl5r7sjis">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-3156" data-mp="36xyq0chnops2">
                    Prossima scadenza
                  </span>
                  <p className="font-medium mt-1" data-id="element-3157" data-mp="2x013pujcx323">
                    {selectedItem.nextMaintenance}
                  </p>
                </div>
                <div className="col-span-full" data-id="element-3158" data-mp="2s17rs6jpxk41">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-3159" data-mp="1llx1y26j2amh">
                    Allegati
                  </span>
                  <div className="mt-1" data-id="element-3160" data-mp="1gkr79ytb4dab">
                    <AttachmentsList files={selectedItem.attachments || []} data-id="element-3161" data-mp="3s9nzoyroi8ec" />
                  </div>
                </div>
                <div className="col-span-full" data-id="element-3162" data-mp="12tkzy41x5es">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-3163" data-mp="13mie8h4eik9">
                    Note
                  </span>
                  <p className="text-sm text-slate-700 mt-1 whitespace-pre-line" data-id="element-3164" data-mp="aswbnrnpa79y">
                    {selectedItem.notes || '-'}
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-3165" data-mp="jtufa8u88cs0">
                <Button variant="flat" color="default" onClick={() => setIsViewOpen(false)} data-id="element-3166" data-mp="jvp3bw0sol0b">
                  Chiudi
                </Button>
                <Button onClick={() => {
              setIsViewOpen(false);
              handleEdit(selectedItem);
            }} startContent={<Edit className="h-4 w-4" data-id="element-3168" data-mp="2vl87sw13jmy2" />} data-id="element-3167" data-mp="2qkahtx9uhom0">
                  Modifica
                </Button>
              </div>
            </div>}
        </Modal>

        <Modal isOpen={isEditOpen} onClose={() => setIsEditOpen(false)} title={selectedItem ? 'Modifica asset' : 'Nuovo asset'} size="lg" data-id="element-3169" data-mp="2lx85g0p28tx2">
          <AnimatePresence mode="wait" data-id="element-3170" data-mp="2c6lw2o7xszmt">
            {saveSuccess ? <SaveSuccessView label="Asset salvato con successo" data-id="element-3171" data-mp="2zveuqsfy1ll4" /> : <motion.div key="form" initial={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} data-id="element-3172" data-mp="34vb0ox87bn3b">
                <div className="space-y-6" data-id="element-3173" data-mp="2uvk50s7wn21n">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3174" data-mp="2uyog89jsey1t">
                    <Input label="Codice" value={formData.code || ''} onChange={e => setFormData({
                  ...formData,
                  code: e.target.value
                })} data-id="element-3175" data-mp="1egsbud2xpqra" />
                    <Select label="Tipologia" value={formData.type || ''} onChange={e => setFormData({
                  ...formData,
                  type: e.target.value
                })} options={[{
                  value: 'Strumento Misura',
                  label: 'Strumento Misura'
                }, {
                  value: 'Hardware',
                  label: 'Hardware'
                }, {
                  value: 'Veicolo',
                  label: 'Veicolo'
                }, {
                  value: 'Attrezzatura',
                  label: 'Attrezzatura'
                }]} required data-id="element-3176" data-mp="ak81qc83zwz4" />
                    <Input label="Nome" value={formData.name || ''} onChange={e => setFormData({
                  ...formData,
                  name: e.target.value
                })} required className="col-span-full" data-id="element-3177" data-mp="13zlvu30qekwi" />
                    <Input label="Responsabile" value={formData.responsible || ''} onChange={e => setFormData({
                  ...formData,
                  responsible: e.target.value
                })} data-id="element-3178" data-mp="l9w1fh0vs154" />
                    <Select label="Tipo manutenzione" value={formData.maintenanceType || ''} onChange={e => setFormData({
                  ...formData,
                  maintenanceType: e.target.value
                })} options={[{
                  value: 'Ordinaria',
                  label: 'Ordinaria'
                }, {
                  value: 'Straordinaria',
                  label: 'Straordinaria'
                }]} data-id="element-3179" data-mp="3m2sft5p34vjg" />
                    <DatePicker label="Prossima scadenza" value={formData.nextMaintenance || ''} onChange={e => setFormData({
                  ...formData,
                  nextMaintenance: e.target.value
                })} data-id="element-3180" data-mp="2oiobd8nnecpj" />
                    <div className="col-span-full" data-id="element-3181" data-mp="3kgfkplh07e4r">
                      <AttachmentsField value={formData.attachments || []} onChange={attachments => setFormData({
                    ...formData,
                    attachments
                  })} data-id="element-3182" data-mp="36hpdhd0gzy2o" />
                    </div>
                    <div className="col-span-full" data-id="element-3183" data-mp="31jxl6hvwy9xy">
                      <Textarea label="Note" value={formData.notes || ''} onChange={e => setFormData({
                    ...formData,
                    notes: e.target.value
                  })} data-id="element-3184" data-mp="31ls63w76q221" />
                    </div>
                  </div>
                  <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-3185" data-mp="2itpreqe3svuf">
                    <Button variant="flat" color="default" onClick={() => setIsEditOpen(false)} data-id="element-3186" data-mp="1z6uuerqwi1y1">
                      Annulla
                    </Button>
                    <Button onClick={handleSave} startContent={<Save className="h-4 w-4" data-id="element-3188" data-mp="245pmyleo74vc" />} data-id="element-3187" data-mp="29757mccxr9d7">
                      Salva asset
                    </Button>
                  </div>
                </div>
              </motion.div>}
          </AnimatePresence>
        </Modal>

        <DeleteModal isOpen={isDeleteOpen} onClose={() => setIsDeleteOpen(false)} onConfirm={confirmDelete} itemLabel={selectedItem?.name || ''} deleteSuccess={deleteSuccess} data-id="element-3189" data-mp="2rvsduy6um4sd" />
      </Layout>;
  }
  // ─── Maintenance Section ──────────────────────────────────────────────────
  if (sectionId === 'maintenance') {
    return <Layout data-id="element-3190" data-mp="33ycw83dswtxt">
        {header}
        {toolbar}
        <Card noPadding className="overflow-hidden" data-id="element-3191" data-mp="33znwht81fjk6">
          <div className="overflow-x-auto" data-id="element-3192" data-mp="38x6a3wx02myi">
            <table className="w-full text-left border-collapse table-auto" data-id="element-3193" data-mp="38y7mq5tnzhic">
              <thead data-id="element-3194" data-mp="3dw92ohvsn0pl">
                <tr className="bg-slate-50/50 border-b border-slate-100 text-xs uppercase tracking-wider text-slate-500 font-semibold" data-id="element-3195" data-mp="3dvr25923m0bv">
                  {cols.filter(c => visibleColumns.includes(c.key)).map(col => <th key={col.key} className="px-4 py-4" data-id="element-3196" data-mp="3rne5ttydeb4o">
                        {col.label}
                      </th>)}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-3197" data-mp="5gh020y7kv43">
                {paginatedData.map(item => <tr key={item.id} className="hover:bg-slate-50/80 transition-colors group" data-id="element-3198" data-mp="229ise7ihyee8">
                    {visibleColumns.includes('code') && <td className="px-4 py-4 font-mono text-xs text-slate-500" data-id="element-3199" data-mp="1zpmipzlynh96">
                        {item.code}
                      </td>}
                    {visibleColumns.includes('equipment') && <td className="px-4 py-4 text-sm font-medium text-slate-900" data-id="element-3200" data-mp="1pz1cbq01p8fp">
                        {item.equipment}
                      </td>}
                    {visibleColumns.includes('type') && <td className="px-4 py-4" data-id="element-3201" data-mp="2doce9at4t8vf">
                        <Badge variant={item.type === 'Ordinaria' ? 'info' : 'warning'} data-id="element-3202" data-mp="2dpngh66xi8kf">
                          {item.type}
                        </Badge>
                      </td>}
                    {visibleColumns.includes('date') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-3203" data-mp="saq501hkwn4n">
                        {item.date}
                      </td>}
                    {visibleColumns.includes('result') && <td className="px-4 py-4" data-id="element-3204" data-mp="1mz15jwbherrj">
                        <Badge variant={item.result === 'Positivo' ? 'success' : 'danger'} data-id="element-3205" data-mp="18ygmcz1nmtxi">
                          {item.result}
                        </Badge>
                      </td>}
                    {visibleColumns.includes('nextDate') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-3206" data-mp="25qtwll6zq2wm">
                        {item.nextDate}
                      </td>}
                    {visibleColumns.includes('responsible') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-3207" data-mp="2joqxuuwe2xhm">
                        {item.responsible}
                      </td>}
                    {visibleColumns.includes('actions') && <td className="px-4 py-4 pr-6" data-id="element-3208" data-mp="cz6rwekz87fn">
                        {actionButtons(item, 'manutenzione')}
                      </td>}
                  </tr>)}
              </tbody>
            </table>
          </div>
          <div className="border-t border-slate-100" data-id="element-3209" data-mp="hqyksvrlup8y">
            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={goToPage} totalItems={totalItems} pageSize={pageSize} onPageSizeChange={setPageSize} data-id="element-3210" data-mp="hs9nes9bdqyh" />
          </div>
        </Card>

        <Modal isOpen={isViewOpen} onClose={() => setIsViewOpen(false)} title={selectedItem ? `Dettaglio manutenzione — ${selectedItem.code}` : ''} size="lg" data-id="element-3211" data-mp="40botbd8bdgk">
          {selectedItem && <div className="space-y-6" data-id="element-3212" data-mp="1ag5hrqbev6s3">
              <div className="grid grid-cols-2 gap-4" data-id="element-3213" data-mp="1af455heqyc89">
                <div data-id="element-3214" data-mp="jdvrye5wlpdj">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-3215" data-mp="jdcbz2ztdb25">
                    Codice
                  </span>
                  <p className="font-mono font-medium mt-1" data-id="element-3216" data-mp="eheu2s8wjwn9">
                    {selectedItem.code}
                  </p>
                </div>
                <div data-id="element-3217" data-mp="ocyjqwwvhz3k">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-3218" data-mp="oeta4qqwyjf1">
                    Esito
                  </span>
                  <div className="mt-1" data-id="element-3219" data-mp="lt4ojyrbaub">
                    <Badge variant={selectedItem.result === 'Positivo' ? 'success' : 'danger'} data-id="element-3220" data-mp="3rv005ugeoi5c">
                      {selectedItem.result}
                    </Badge>
                  </div>
                </div>
                <div className="col-span-full" data-id="element-3221" data-mp="3d37vqdi5goie">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-3222" data-mp="394iluuwblp3i">
                    Asset
                  </span>
                  <p className="font-medium mt-1" data-id="element-3223" data-mp="2yyhc8e4cot2w">{selectedItem.equipment}</p>
                </div>
                <div data-id="element-3224" data-mp="33rtodoruf7er">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-3225" data-mp="33sbwpjrs5c31">
                    Tipo
                  </span>
                  <div className="mt-1" data-id="element-3226" data-mp="2tzwcsgwxadfi">
                    <Badge variant={selectedItem.type === 'Ordinaria' ? 'info' : 'warning'} data-id="element-3227" data-mp="2u0z0kfyy7zr7">
                      {selectedItem.type}
                    </Badge>
                  </div>
                </div>
                <div data-id="element-3228" data-mp="37rug2b3ydf3r">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-3229" data-mp="37ugoecyo3yn1">
                    Data esecuzione
                  </span>
                  <p className="font-medium mt-1" data-id="element-3230" data-mp="1wbvy1hqsriqe">{selectedItem.date}</p>
                </div>
                <div data-id="element-3231" data-mp="21ca6v2082wph">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-3232" data-mp="21dldd9l1yika">
                    Prossima scadenza
                  </span>
                  <p className="font-medium mt-1" data-id="element-3233" data-mp="2ugj2ql4kdg8">{selectedItem.nextDate}</p>
                </div>
                <div data-id="element-3234" data-mp="3u8maowpxh8vi">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-3235" data-mp="3u843x65n3c9c">
                    Responsabile
                  </span>
                  <p className="font-medium mt-1" data-id="element-3236" data-mp="3p9j61ddv14o2">{selectedItem.responsible}</p>
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-3237" data-mp="3k8dcxegaffe0">
                <Button variant="flat" color="default" onClick={() => setIsViewOpen(false)} data-id="element-3238" data-mp="3gb7hri0guiys">
                  Chiudi
                </Button>
                <Button onClick={() => {
              setIsViewOpen(false);
              handleEdit(selectedItem);
            }} startContent={<Edit className="h-4 w-4" data-id="element-3240" data-mp="14hhasrn1rgkf" />} data-id="element-3239" data-mp="19in52twcvbw2">
                  Modifica
                </Button>
              </div>
            </div>}
        </Modal>

        <Modal isOpen={isEditOpen} onClose={() => setIsEditOpen(false)} title={selectedItem ? 'Modifica manutenzione' : 'Nuova manutenzione'} size="lg" data-id="element-3241" data-mp="1h0laonbf22u0">
          <AnimatePresence mode="wait" data-id="element-3242" data-mp="1qrg0u2rs9mkq">
            {saveSuccess ? <SaveSuccessView label="Manutenzione salvata con successo" data-id="element-3243" data-mp="t5a8ynghou2c" /> : <motion.div key="form" initial={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} data-id="element-3244" data-mp="y2ii5yjntnrg">
                <div className="space-y-6" data-id="element-3245" data-mp="1829apuontsow">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3246" data-mp="2ehk243hi1a8k">
                    <Input label="Codice" value={formData.code || ''} onChange={e => setFormData({
                  ...formData,
                  code: e.target.value
                })} data-id="element-3247" data-mp="2ehacwi4a373x" />
                    <Input label="Asset" value={formData.equipment || ''} onChange={e => setFormData({
                  ...formData,
                  equipment: e.target.value
                })} required data-id="element-3248" data-mp="2r0p7b8ojtu23" />
                    <Select label="Tipo" value={formData.type || ''} onChange={e => setFormData({
                  ...formData,
                  type: e.target.value
                })} options={[{
                  value: 'Ordinaria',
                  label: 'Ordinaria'
                }, {
                  value: 'Straordinaria',
                  label: 'Straordinaria'
                }]} required data-id="element-3249" data-mp="39r6u6wmp9vfj" />
                    <DatePicker label="Data esecuzione" value={formData.date || ''} onChange={e => setFormData({
                  ...formData,
                  date: e.target.value
                })} data-id="element-3250" data-mp="3tb5sv5u57ymy" />
                    <Select label="Esito" value={formData.result || ''} onChange={e => setFormData({
                  ...formData,
                  result: e.target.value
                })} options={[{
                  value: 'Positivo',
                  label: 'Positivo'
                }, {
                  value: 'Negativo',
                  label: 'Negativo'
                }]} required data-id="element-3251" data-mp="3alfy2iqdrcv1" />
                    <DatePicker label="Prossima scadenza" value={formData.nextDate || ''} onChange={e => setFormData({
                  ...formData,
                  nextDate: e.target.value
                })} data-id="element-3252" data-mp="30gps81ruvolj" />
                    <Input label="Responsabile" value={formData.responsible || ''} onChange={e => setFormData({
                  ...formData,
                  responsible: e.target.value
                })} className="col-span-full" data-id="element-3253" data-mp="1f4y2q3fu8ox9" />
                  </div>
                  <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-3254" data-mp="3vptda98y35t9">
                    <Button variant="flat" color="default" onClick={() => setIsEditOpen(false)} data-id="element-3255" data-mp="4e1cu0vpb2g9">
                      Annulla
                    </Button>
                    <Button onClick={handleSave} startContent={<Save className="h-4 w-4" data-id="element-3257" data-mp="iaexfivj7jyy" />} data-id="element-3256" data-mp="ichj5qpniqxv">
                      Salva manutenzione
                    </Button>
                  </div>
                </div>
              </motion.div>}
          </AnimatePresence>
        </Modal>

        <DeleteModal isOpen={isDeleteOpen} onClose={() => setIsDeleteOpen(false)} onConfirm={confirmDelete} itemLabel={selectedItem?.code || ''} deleteSuccess={deleteSuccess} data-id="element-3258" data-mp="2p37iblktl86j" />
      </Layout>;
  }
  // Fallback
  return <Layout data-id="element-3259" data-mp="14jlp5bfade5a">
      {header}
      <Card data-id="element-3260" data-mp="14jvhh5nszxe5">
        <p className="text-slate-500 text-center py-8" data-id="element-3261" data-mp="14mhj6oo0jiog">
          Sezione non disponibile.
        </p>
      </Card>
    </Layout>;
}
// ─── Section Filters Component ────────────────────────────────────────────────
function SectionFilters({
  sectionId,
  filters,
  setFilter
}: {
  sectionId: string;
  filters: any;
  setFilter: (key: string, value: any) => void;
}) {
  switch (sectionId) {
    case 'workers':
      return <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4" data-id="element-3262" data-mp="guj742gqpja9">
          <Select label="Stato formazione" value={filters.trainingStatus || ''} onChange={e => setFilter('trainingStatus', e.target.value)} options={[{
          value: '',
          label: 'Tutti'
        }, {
          value: 'valid',
          label: 'In regola'
        }, {
          value: 'expiring',
          label: 'In scadenza'
        }, {
          value: 'expired',
          label: 'Scaduto'
        }]} data-id="element-3263" data-mp="1x9as3ubk3o9j" />
          <Select label="Sorveglianza" value={filters.healthStatus || ''} onChange={e => setFilter('healthStatus', e.target.value)} options={[{
          value: '',
          label: 'Tutti'
        }, {
          value: 'fit',
          label: 'Idoneo'
        }, {
          value: 'unfit',
          label: 'Non idoneo'
        }]} data-id="element-3264" data-mp="2ijyijouete30" />
        </div>;
    case 'deadlines':
      return <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4" data-id="element-3265" data-mp="blexganfwvhi">
          <Select label="Tipologia" value={filters.type || ''} onChange={e => setFilter('type', e.target.value)} options={[{
          value: '',
          label: 'Tutte'
        }, {
          value: 'Riunione annuale',
          label: 'Riunione annuale'
        }, {
          value: 'Prova di emergenza',
          label: 'Prova di emergenza'
        }, {
          value: 'Tampone alimentare',
          label: 'Tampone alimentare'
        }, {
          value: 'Audit interno',
          label: 'Audit interno'
        }, {
          value: 'Report annuale',
          label: 'Report annuale'
        }]} data-id="element-3266" data-mp="bjca5ydo9ggh" />
          <Select label="Stato" value={filters.status || ''} onChange={e => setFilter('status', e.target.value)} options={[{
          value: '',
          label: 'Tutti'
        }, {
          value: 'Urgente',
          label: 'Urgente'
        }, {
          value: 'In scadenza',
          label: 'In scadenza'
        }, {
          value: 'Pianificata',
          label: 'Pianificata'
        }, {
          value: 'Completata',
          label: 'Completata'
        }]} data-id="element-3267" data-mp="1hzo1bfi2ty2k" />
          <Select label="Responsabile" value={filters.responsible || ''} onChange={e => setFilter('responsible', e.target.value)} options={[{
          value: '',
          label: 'Tutti'
        }, {
          value: 'Alessio',
          label: 'Alessio'
        }, {
          value: 'Lorenzo',
          label: 'Lorenzo'
        }, {
          value: 'Gian Franco',
          label: 'Gian Franco'
        }, {
          value: 'Tiziano',
          label: 'Tiziano'
        }]} data-id="element-3268" data-mp="29wp5uwyiru3y" />
        </div>;
    case 'contracts':
      return <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4" data-id="element-3269" data-mp="2gbxf4n12ahpy">
          <Select label="Tipo" value={filters.type || ''} onChange={e => setFilter('type', e.target.value)} options={[{
          value: '',
          label: 'Tutti'
        }, {
          value: 'Contratto',
          label: 'Contratto'
        }, {
          value: 'Offerta',
          label: 'Offerta'
        }]} data-id="element-3270" data-mp="2l8wbzdc412ru" />
          <Select label="Stato" value={filters.status || ''} onChange={e => setFilter('status', e.target.value)} options={[{
          value: '',
          label: 'Tutti'
        }, {
          value: 'Attivo',
          label: 'Attivo'
        }, {
          value: 'In attesa',
          label: 'In attesa'
        }, {
          value: 'Bozza',
          label: 'Bozza'
        }, {
          value: 'Scaduto',
          label: 'Scaduto'
        }]} data-id="element-3271" data-mp="sc23pypl962t" />
        </div>;
    case 'invoices':
      return <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4" data-id="element-3272" data-mp="3n1s2nzvzjc0r">
          <Select label="Stato" value={filters.status || ''} onChange={e => setFilter('status', e.target.value)} options={[{
          value: '',
          label: 'Tutti'
        }, {
          value: 'Pagata',
          label: 'Pagata'
        }, {
          value: 'In attesa',
          label: 'In attesa'
        }, {
          value: 'Bozza',
          label: 'Bozza'
        }, {
          value: 'Scaduta',
          label: 'Scaduta'
        }]} data-id="element-3273" data-mp="3mzno8o0gmum8" />
        </div>;
    case 'training':
      return <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4" data-id="element-3274" data-mp="3d280g0dp3js9">
          <Select label="Tipologia" value={filters.type || ''} onChange={e => setFilter('type', e.target.value)} options={[{
          value: '',
          label: 'Tutte'
        }, {
          value: 'Sicurezza',
          label: 'Sicurezza'
        }, {
          value: 'HACCP',
          label: 'HACCP'
        }, {
          value: 'Privacy',
          label: 'Privacy'
        }]} data-id="element-3275" data-mp="3i1b178mn73uf" />
          <Select label="Stato" value={filters.status || ''} onChange={e => setFilter('status', e.target.value)} options={[{
          value: '',
          label: 'Tutti'
        }, {
          value: 'In corso',
          label: 'In corso'
        }, {
          value: 'Programmato',
          label: 'Programmato'
        }, {
          value: 'Completato',
          label: 'Completato'
        }]} data-id="element-3276" data-mp="11fed8rp1rodj" />
          <Select label="Modalità" value={filters.mode || ''} onChange={e => setFilter('mode', e.target.value)} options={[{
          value: '',
          label: 'Tutte'
        }, {
          value: 'Sede SEF',
          label: 'Sede SEF'
        }, {
          value: 'Sede cliente',
          label: 'Sede cliente'
        }, {
          value: 'A distanza',
          label: 'A distanza'
        }]} data-id="element-3277" data-mp="9nf6rbea536n" />
        </div>;
    case 'operations':
      return <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4" data-id="element-3278" data-mp="3eluh571lj3f2">
          <Select label="Ambito" value={filters.ambito || ''} onChange={e => setFilter('ambito', e.target.value)} options={[{
          value: '',
          label: 'Tutti'
        }, {
          value: 'Sicurezza',
          label: 'Sicurezza'
        }, {
          value: 'Privacy',
          label: 'Privacy'
        }, {
          value: 'HACCP',
          label: 'HACCP'
        }, {
          value: 'Ambiente',
          label: 'Ambiente'
        }]} data-id="element-3279" data-mp="3ejq3hxdwaq1l" />
          <Select label="Stato" value={filters.status || ''} onChange={e => setFilter('status', e.target.value)} options={[{
          value: '',
          label: 'Tutti'
        }, {
          value: 'da_pianificare',
          label: 'Da pianificare'
        }, {
          value: 'pianificato',
          label: 'Pianificata'
        }, {
          value: 'calendarizzato',
          label: 'Calendarizzata'
        }, {
          value: 'in_svolgimento_attivo',
          label: 'Attiva'
        }, {
          value: 'in_svolgimento_sospeso',
          label: 'Sospesa'
        }, {
          value: 'completo',
          label: 'Completa'
        }]} data-id="element-3280" data-mp="2v25dx0xyvzkq" />
          <Select label="Operatore" value={filters.operator || ''} onChange={e => setFilter('operator', e.target.value)} options={[{
          value: '',
          label: 'Tutti'
        }, {
          value: 'Alessio',
          label: 'Alessio'
        }, {
          value: 'Lorenzo',
          label: 'Lorenzo'
        }, {
          value: 'Gian Franco',
          label: 'Gian Franco'
        }, {
          value: 'Tiziano',
          label: 'Tiziano'
        }]} data-id="element-3281" data-mp="1cu7v4rt9zevs" />
        </div>;
    case 'non_conformities':
      return <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4" data-id="element-3282" data-mp="3l2me0gou8kj7">
          <Select label="Tipologia" value={filters.type || ''} onChange={e => setFilter('type', e.target.value)} options={[{
          value: '',
          label: 'Tutte'
        }, {
          value: 'Non conformità maggiore',
          label: 'NC Maggiore'
        }, {
          value: 'Non conformità minore',
          label: 'NC Minore'
        }, {
          value: 'Osservazione',
          label: 'Osservazione'
        }]} data-id="element-3283" data-mp="3g3t1orlaezkm" />
          <Select label="Origine" value={filters.origin || ''} onChange={e => setFilter('origin', e.target.value)} options={[{
          value: '',
          label: 'Tutte'
        }, {
          value: 'Interna',
          label: 'Interna'
        }, {
          value: 'Cliente (reclamo)',
          label: 'Cliente (reclamo)'
        }, {
          value: 'Fornitore',
          label: 'Fornitore'
        }]} data-id="element-3284" data-mp="2ne40ad4o0xih" />
          <Select label="Stato" value={filters.status || ''} onChange={e => setFilter('status', e.target.value)} options={[{
          value: '',
          label: 'Tutti'
        }, {
          value: 'Aperta',
          label: 'Aperta'
        }, {
          value: 'Sospesa',
          label: 'Sospesa'
        }, {
          value: 'Chiusa',
          label: 'Chiusa'
        }]} data-id="element-3285" data-mp="1v7tivcup9mh5" />
        </div>;
    case 'improvements':
      return <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4" data-id="element-3286" data-mp="14vq4niiabc1l">
          <Select label="Stato" value={filters.status || ''} onChange={e => setFilter('status', e.target.value)} options={[{
          value: '',
          label: 'Tutti'
        }, {
          value: 'Approvato',
          label: 'Approvato'
        }, {
          value: 'In valutazione',
          label: 'In valutazione'
        }, {
          value: 'Sospesa',
          label: 'Sospesa'
        }, {
          value: 'Scartata',
          label: 'Scartata'
        }]} data-id="element-3287" data-mp="10500g8llhf8h" />
          <Select label="Responsabile" value={filters.responsible || ''} onChange={e => setFilter('responsible', e.target.value)} options={[{
          value: '',
          label: 'Tutti'
        }, {
          value: 'Alessio',
          label: 'Alessio'
        }, {
          value: 'Lorenzo',
          label: 'Lorenzo'
        }, {
          value: 'Gian Franco',
          label: 'Gian Franco'
        }, {
          value: 'Tiziano',
          label: 'Tiziano'
        }]} data-id="element-3288" data-mp="26kttsonovakj" />
        </div>;
    case 'equipment':
      return <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4" data-id="element-3289" data-mp="inay06ui3x7a">
          <Select label="Tipologia" value={filters.type || ''} onChange={e => setFilter('type', e.target.value)} options={[{
          value: '',
          label: 'Tutte'
        }, {
          value: 'Strumento Misura',
          label: 'Strumento Misura'
        }, {
          value: 'Hardware',
          label: 'Hardware'
        }, {
          value: 'Veicolo',
          label: 'Veicolo'
        }, {
          value: 'Attrezzatura',
          label: 'Attrezzatura'
        }]} data-id="element-3290" data-mp="ipdyjkta58q9" />
          <Select label="Tipo manutenzione" value={filters.maintenanceType || ''} onChange={e => setFilter('maintenanceType', e.target.value)} options={[{
          value: '',
          label: 'Tutti'
        }, {
          value: 'Ordinaria',
          label: 'Ordinaria'
        }, {
          value: 'Straordinaria',
          label: 'Straordinaria'
        }]} data-id="element-3291" data-mp="xqufsbsnz4eu" />
        </div>;
    case 'maintenance':
      return <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4" data-id="element-3292" data-mp="wnvqv3qugpku">
          <Select label="Tipo" value={filters.type || ''} onChange={e => setFilter('type', e.target.value)} options={[{
          value: '',
          label: 'Tutti'
        }, {
          value: 'Ordinaria',
          label: 'Ordinaria'
        }, {
          value: 'Straordinaria',
          label: 'Straordinaria'
        }]} data-id="element-3293" data-mp="11ni3pc2rg3t4" />
          <Select label="Esito" value={filters.result || ''} onChange={e => setFilter('result', e.target.value)} options={[{
          value: '',
          label: 'Tutti'
        }, {
          value: 'Positivo',
          label: 'Positivo'
        }, {
          value: 'Negativo',
          label: 'Negativo'
        }]} data-id="element-3294" data-mp="1pd2rc89uzp8q" />
        </div>;
    default:
      return null;
  }
}